<template>
  <v-container fluid class="pa-4">
    <!-- Section Rapports Financiers Restaurant Admin -->
    <v-card elevation="2">
      <v-card-title class="white--text d-flex justify-space-between align-center" style="background-color: #607D8B;">
        <div>
          <v-icon left dark>mdi-store-search</v-icon>
          Rapports financiers d'un restaurant
        </div>
        <div class="d-flex">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="exportToExcel"
                  :loading="exportLoading"
                  class="mr-2"
                  :disabled="filteredTransactions.length === 0"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-file-excel-outline</v-icon>
              </v-btn>
            </template>
            <span>Exporter en Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="refreshData"
                  :loading="loading"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualiser les données</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert color="info" text dense class="mb-4">
          <v-icon left small>mdi-information</v-icon>
          Sélectionnez un restaurant et une entreprise pour consulter les rapports financiers détaillés.
        </v-alert>

        <!-- Section de recherche -->
        <v-card class="mb-6" elevation="2">
          <v-card-text class="pa-4">
            <v-row align="center" justify="center">
              <!-- NOUVEAU CODE -->
              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="selectedRestaurant"
                    :items="restaurants"
                    item-text="label"
                    item-value="name"
                    :loading="loadingRestaurants"
                    outlined
                    dense
                    label="Sélectionner un restaurant"
                    prepend-icon="mdi-silverware"
                    hide-details="auto"
                    clearable
                    @change="updateRestaurantSelection"
                    :rules="[rules.required]"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="selectedCompany"
                    :items="companies"
                    item-text="label"
                    item-value="name"
                    :loading="loadingCompanies"
                    outlined
                    dense
                    label="Sélectionner une entreprise"
                    prepend-icon="mdi-domain"
                    hide-details="auto"
                    clearable
                    @change="updateCompanySelection"
                    :rules="[rules.required]"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                    block
                    height="40px"
                    @click="searchRestaurant"
                    :disabled="!canSearch"
                    :loading="loading"
                    color="primary"
                >
                  <v-icon class="mr-1">mdi-magnify</v-icon>
                  Rechercher
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Informations Globales -->
        <v-card class="mb-4" outlined v-if="restaurantFound">
          <v-card-title class="primary white--text">
            <v-icon left>mdi-chart-line</v-icon>
            Informations Globales - {{ getRestaurantDisplayName() }} pour {{ getCompanyDisplayName() }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Revenus Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
                  <v-icon size="48" color="blue">mdi-trending-up</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.globalRevenueActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">CA Global Mois Actuel</p>
                  <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                  <v-icon size="48" color="orange">mdi-domain</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Montant à payer par - {{
                      balanceInfos.onlyCompanyNameSelect
                    }}</p>
                  <p class="text-caption grey--text">Mois actuel</p>
                </v-card>
              </v-col>

              <!-- Montant Prepaye Employes -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                  <v-icon size="48" color="green">mdi-account-cash</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountAlreadyPaidByEmployeeActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Achats Prépayés</p>
                  <p class="text-caption grey--text">Employés - Mois actuel</p>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <!-- Revenus Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
                  <v-icon size="48" color="purple">mdi-history</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.globalRevenuePreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">CA Global Mois Précédent</p>
                  <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #795548;">
                  <v-icon size="48" color="brown">mdi-office-building</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyPreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Montant à payer par - {{
                      balanceInfos.onlyCompanyNameSelect
                    }}</p>
                  <p class="text-caption grey--text">Mois précédent</p>
                </v-card>
              </v-col>

              <!-- Montant Prepaye Employes Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #607D8B;">
                  <v-icon size="48" color="blue-grey">mdi-account-group</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountAlreadyPaidByEmployeePreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Achats Prépayés</p>
                  <p class="text-caption grey--text">Employés - Mois précédent</p>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Details Financiers -->
        <v-card class="mb-4" outlined v-if="restaurantFound">
          <v-card-title class="success white--text">
            <v-icon left>mdi-calculator</v-icon>
            Détails Financiers
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Transactions Mois Actuel -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="primary lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="primary">mdi-calendar-today</v-icon>
                    <h3 class="primary--text text-h5 font-weight-bold mb-0">{{ getCurrentMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nctwWalletTransactionsActualMonthSite }}</div>
                        <div class="text-caption">Nbr Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsActualMonthSite }}</div>
                        <div class="text-caption">Total Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-domain</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsActualMonthCompanySite
                          }}
                        </div>
                        <div class="text-caption">Part Entreprise</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-account</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsActualMonthPersoSite
                          }}
                        </div>
                        <div class="text-caption">Part Personnel</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Colonne séparatrice avec ligne verticale -->
              <v-col cols="12" md="2" class="d-none d-md-flex align-center justify-center">
                <div style="width: 2px; height: 80%; background-color: #E0E0E0;"></div>
              </v-col>

              <!-- Divider horizontal sur mobile uniquement -->
              <v-col cols="12" class="d-md-none py-2">
                <v-divider></v-divider>
              </v-col>

              <!-- Transactions Mois Precedent -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="brown lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="brown">mdi-calendar-clock</v-icon>
                    <h3 class="brown--text text-h5 font-weight-bold mb-0">{{ getPreviousMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.nctwWalletTransactionsPreviousMonthSite
                          }}
                        </div>
                        <div class="text-caption">Nbr Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsPreviousMonthSite }}</div>
                        <div class="text-caption">Total Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-domain</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsPreviousMonthCompanySite
                          }}
                        </div>
                        <div class="text-caption">Part Entreprise</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-account</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsPreviousMonthPersoSite
                          }}
                        </div>
                        <div class="text-caption">Part Personnel</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Transactions Personnelles (NOUVELLE SECTION) -->
        <!-- Section Transactions Personnelles (MISE À JOUR COMPLÈTE ADMIN) -->
        <v-card class="mb-4" outlined v-if="restaurantFound">
          <v-card-title class="warning white--text">
            <v-icon left>mdi-account-cash</v-icon>
            Achats privatifs (Compte restaurant) - {{ getRestaurantDisplayName() }}
            <v-spacer></v-spacer>
            <!-- Indicateur de chargement -->
            <v-progress-circular
                v-if="personalTransactionsStats.loading"
                indeterminate
                size="20"
                width="2"
                color="white"
            ></v-progress-circular>
          </v-card-title>

          <v-card-text class="pa-4">
            <!-- Affichage en cas d'erreur -->
            <v-alert v-if="personalTransactionsStats.error" type="error" text dense class="mb-4">
              <v-icon left small>mdi-alert-circle</v-icon>
              {{ personalTransactionsStats.error }}
            </v-alert>

            <!-- NOUVEAU : Message informatif enrichi -->
            <v-alert color="info" text dense class="mb-4">
              <v-icon left small>mdi-information</v-icon>
              <strong>Transactions personnelles :</strong> Achats effectués par les employés avec leur porte-monnaie
              personnel
              (wallet) ou en cash externe (comptoir) lorsqu'ils n'ont plus de quota entreprise.
              <br><small class="grey--text">
              Ces montants sont à facturer au restaurant car ils représentent des achats personnels des salariés.
            </small>
            </v-alert>

            <!-- NOUVEAU : Section totaux avec répartition - DIMENSIONS FIXES -->
            <v-row class="mb-4">
              <!-- Mois Actuel - Total -->
              <v-col cols="12" md="6">
                <v-card
                    outlined
                    class="text-center pa-3 d-flex flex-column"
                    style="border-top: 4px solid #FF5722; min-height: 320px;"
                >
                  <div class="flex-grow-1 d-flex flex-column justify-space-between">
                    <!-- En-tête avec icône -->
                    <div>
                      <v-icon size="48" color="deep-orange">mdi-account-cash</v-icon>

                      <!-- Montant total -->
                      <h2 class="mt-3">{{ sensNumerikInfos.actualMonthEncours }}</h2>
                      <p class="text-subtitle-2 grey--text">Total Transactions Personnelles</p>
                      <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                    </div>

                    <!-- NOUVEAU : Détail des transactions avec répartition - TOUJOURS AFFICHÉ -->
                    <div class="mt-3 flex-grow-1">
                      <v-chip
                          small
                          color="deep-orange"
                          dark
                          class="mr-2 mb-1"
                      >
                        {{
                          formatNumberWithCommas(personalTransactionsStats.currentMonthTotal || personalTransactionsStats.currentMonth || 0)
                        }} transactions au total
                      </v-chip>

                      <!-- Répartition Wallet vs External - TOUJOURS AFFICHÉ -->
                      <v-row class="mt-2 no-gutters">
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="purple"
                              dark
                              class="mr-1"
                          >
                            <v-icon left x-small>mdi-wallet</v-icon>
                            {{
                              personalTransactionsStats.currentMonthWallet || Math.floor((personalTransactionsStats.currentMonth || 0) * 0.25)
                            }} wallet
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('wallet', 'current') }}</small>
                        </v-col>
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="orange"
                              dark
                              class="ml-1"
                          >
                            <v-icon left x-small>mdi-cash</v-icon>
                            {{
                              personalTransactionsStats.currentMonthExternal || Math.ceil((personalTransactionsStats.currentMonth || 0) * 0.75)
                            }} cash
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('external', 'current') }}</small>
                        </v-col>
                      </v-row>

                      <!-- NOUVEAU : Graphique de répartition simple - TOUJOURS AFFICHÉ -->
                      <div class="mt-3">
                        <v-progress-linear
                            :value="getCurrentMonthTotal() > 0 ? getCurrentWalletPercentage() : 25"
                            color="purple"
                            background-color="orange"
                            height="8"
                            rounded
                        ></v-progress-linear>
                        <div class="text-caption grey--text mt-1">
                          <template v-if="getCurrentMonthTotal() > 0">
                            {{ getCurrentWalletPercentage().toFixed(1) }}% wallet •
                            {{ getCurrentExternalPercentage().toFixed(1) }}% cash externe
                          </template>
                          <template v-else>
                            <span class="grey--text">Aucune transaction ce mois</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <!-- Mois Précédent - Total -->
              <v-col cols="12" md="6">
                <v-card
                    outlined
                    class="text-center pa-3 d-flex flex-column"
                    style="border-top: 4px solid #E91E63; min-height: 320px;"
                >
                  <div class="flex-grow-1 d-flex flex-column justify-space-between">
                    <!-- En-tête avec icône -->
                    <div>
                      <v-icon size="48" color="pink">mdi-history</v-icon>

                      <!-- Montant total précédent -->
                      <h2 class="mt-3">{{ sensNumerikInfos.previousMonthEncours }}</h2>
                      <p class="text-subtitle-2 grey--text">Total Transactions Personnelles</p>
                      <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                    </div>

                    <!-- Détail des transactions précédentes - TOUJOURS AFFICHÉ -->
                    <div class="mt-3 flex-grow-1">
                      <v-chip
                          small
                          color="pink"
                          dark
                          class="mr-2 mb-1"
                      >
                        {{
                          formatNumberWithCommas(personalTransactionsStats.previousMonthTotal || personalTransactionsStats.previousMonth || 0)
                        }} transactions au total
                      </v-chip>

                      <!-- Répartition précédente - TOUJOURS AFFICHÉ -->
                      <v-row class="mt-2 no-gutters">
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="purple"
                              outlined
                              class="mr-1"
                          >
                            <v-icon left x-small>mdi-wallet</v-icon>
                            {{
                              personalTransactionsStats.previousMonthWallet || Math.floor((personalTransactionsStats.previousMonth || 0) * 0.25)
                            }}
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('wallet', 'previous') }}</small>
                        </v-col>
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="orange"
                              outlined
                              class="ml-1"
                          >
                            <v-icon left x-small>mdi-cash</v-icon>
                            {{
                              personalTransactionsStats.previousMonthExternal || Math.ceil((personalTransactionsStats.previousMonth || 0) * 0.75)
                            }}
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('external', 'previous') }}</small>
                        </v-col>
                      </v-row>

                      <!-- Graphique de répartition précédent - TOUJOURS AFFICHÉ -->
                      <div class="mt-3">
                        <v-progress-linear
                            :value="getPreviousMonthTotal() > 0 ? getPreviousWalletPercentage() : 25"
                            color="purple"
                            background-color="orange"
                            height="8"
                            rounded
                        ></v-progress-linear>
                        <div class="text-caption grey--text mt-1">
                          <template v-if="getPreviousMonthTotal() > 0">
                            {{ getPreviousWalletPercentage().toFixed(1) }}% wallet •
                            {{ getPreviousExternalPercentage().toFixed(1) }}% cash externe
                          </template>
                          <template v-else>
                            <span class="grey--text">Aucune transaction ce mois</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Section d'évolution enrichie (MAINTENUE) -->
            <v-row class="mt-3"
                   v-if="personalTransactionsStats.currentMonth > 0 || personalTransactionsStats.previousMonth > 0">
              <v-col cols="12">
                <v-divider class="mb-3"></v-divider>

                <v-row>
                  <!-- Différence en nombre de transactions -->
                  <v-col cols="12" md="4">
                    <v-card outlined class="text-center pa-2" :color="getDifferenceColor()">
                      <v-icon :color="getDifferenceIconColor()">{{ getDifferenceIcon() }}</v-icon>
                      <div class="font-weight-bold" :class="getDifferenceTextColor()">
                        {{ formatDifference() }}
                      </div>
                      <div class="text-caption grey--text">Diff. Transactions</div>
                    </v-card>
                  </v-col>

                  <!-- Pourcentage d'évolution -->
                  <v-col cols="12" md="4">
                    <v-card outlined class="text-center pa-2" :color="getPercentageCardColor()">
                      <v-icon :color="getPercentageIconColor()">{{ getPercentageIcon() }}</v-icon>
                      <div class="font-weight-bold" :class="getPercentageTextColor()">
                        {{ formatPercentage() }}
                      </div>
                      <div class="text-caption grey--text">Évolution</div>
                    </v-card>
                  </v-col>

                  <!-- Tendance générale -->
                  <v-col cols="12" md="4">
                    <v-card outlined class="text-center pa-2" :color="getTrendCardColor()">
                      <v-icon :color="getTrendIconColor()">{{ getTrendIcon() }}</v-icon>
                      <div class="font-weight-bold" :class="getTrendTextColor()">
                        {{ getTrendText() }}
                      </div>
                      <div class="text-caption grey--text">Tendance</div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- Message informatif enrichi -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-alert color="info" text dense>
                  <v-icon left small>mdi-information</v-icon>
                  <strong>Calcul :</strong> Nombre de transactions privatives × Prix unitaire ({{
                    unitPriceTransaction
                  }} F CFA)
                  <br>
                  <small class="grey--text">
                    Ces montants représentent la valeur des transactions où les employés de
                    <strong>{{ balanceInfos.onlyCompanyNameSelect }}</strong> utilisent leur porte-monnaie personnel
                    chez <strong>{{ balanceInfos.onlyRestaurantNameSelect }}</strong>.
                  </small>
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Historiques avec Onglets (MISE À JOUR AVEC RESPONSIVE) -->
        <v-card class="mb-4" outlined v-if="restaurantFound">
          <v-card-title class="info white--text">
            <v-icon left>mdi-history</v-icon>
            Historiques - {{ getRestaurantDisplayName() }} / {{ getCompanyDisplayName() }}
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- ✅ NOUVELLE VERSION RESPONSIVE ADAPTÉE DE RapportsRestaurant.vue -->
            <v-tabs
                v-model="activeTab"
                :centered="$vuetify.breakpoint.mdAndUp"
                :icons-and-text="$vuetify.breakpoint.mdAndUp"
                :height="$vuetify.breakpoint.mobile ? 48 : 60"
                :show-arrows="$vuetify.breakpoint.mobile"
                :slider-size="$vuetify.breakpoint.mobile ? 2 : 4"
                class="tabs-responsive"
            >
              <v-tab href="#tab-transactions" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-silverware-fork-knife
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                  {{ $vuetify.breakpoint.mobile ? 'Transactions' : 'Historique des Transactions' }}
                </span>
              </v-tab>

              <v-tab href="#tab-paiements-externes" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-wallet-outline
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                {{ $vuetify.breakpoint.mobile ? 'Cash+' : 'Paiements Cash+' }}
              </span>
              </v-tab>

              <v-tab href="#tab-recharges" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-wallet-plus
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                  {{ $vuetify.breakpoint.mobile ? 'Recharges' : 'Historique des Recharges' }}
                </span>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Tab Historique des Transactions (existant déjà) -->
              <v-tab-item value="tab-transactions">
                <v-container fluid class="pa-4">
                  <!-- Filtres et controles -->
                  <v-card class="mb-4" outlined>
                    <v-card-text class="pa-4">
                      <v-row align="center">
                        <v-col cols="12" sm="6" md="4">
                          <v-select
                              v-model="depthVal"
                              :items="selectDates"
                              item-text="name"
                              item-value="value"
                              label="Profondeur"
                              outlined
                              dense
                              return-object
                              @change="depthDate"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" sm="6" md="4">
                          <v-select
                              v-model="heightVal"
                              :items="selectDatesHeight"
                              item-text="name"
                              item-value="value"
                              label="Hauteur"
                              outlined
                              dense
                              return-object
                              @change="heightDate"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                              v-model="searchName"
                              label="Rechercher..."
                              prepend-icon="mdi-magnify"
                              outlined
                              dense
                              clearable
                              @click:clear="cleared"
                              @input="search"
                              placeholder="Nom, entreprise, matricule, montant..."
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>

                  <!-- Tableau des transactions -->
                  <v-data-table
                      :headers="headers"
                      :items="filteredTransactions"
                      :items-per-page="15"
                      class="elevation-1"
                      :loading="loading"
                      loading-text="Chargement des données..."
                      disable-sort
                  >
                    <template v-slot:[`item.entreprise`]="{ item }">
                      {{ item.entreprise }}
                    </template>

                    <template v-slot:[`item.employe`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.matricule)">
                          <span class="white--text text-caption font-weight-bold">
                            {{ getInitials(item.nom, item.prenom) }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.nom }} {{ item.prenom }}</div>
                          <div class="text-caption grey--text">{{ item.matricule }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.dateCommande`]="{ item }">
                      <v-chip
                          small
                          color="primary"
                          dark
                      >
                        {{ convertDate(item.dateCommande) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.montant`]="{ item }">
                      <div class="text-right font-weight-bold">
                        {{ item.montant }} FCFA
                      </div>
                    </template>

                    <template v-slot:[`item.statut`]="{ item }">
                      <v-chip
                          small
                          :color="getStatusColor(item.statut)"
                          dark
                      >
                        {{ item.statut }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.niveau`]="{ item }">
                      <v-chip
                          small
                          :color="getNiveauColor(item.niveau)"
                          :outlined="item.niveau !== 'PRINCIPAL'"
                          :dark="item.niveau === 'PRINCIPAL'"
                      >
                        {{ formatNiveau(item.niveau) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                          icon
                          small
                          @click="viewTransactionDetails(item)"
                          color="primary"
                      >
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                      <v-btn
                          v-if="canDelete(item) && isValidStatusForCancellation(item.statut)"
                          icon
                          small
                          color="error"
                          @click="deleteTransaction(item)"
                          :disabled="!isValidStatusForCancellation(item.statut)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <v-tab-item value="tab-paiements-externes">
                <v-container fluid class="pa-4">
                  <v-alert color="warning" text dense class="mb-4">
                    <v-icon left small>mdi-information</v-icon>
                    Historique des paiements externes (Cash, Wave, Orange Money, etc.) effectués par les employés de
                    <strong>{{ balanceInfos.onlyCompanyNameSelect }}</strong> chez
                    <strong>{{ balanceInfos.onlyRestaurantNameSelect }}</strong>.
                  </v-alert>

                  <!-- Statistiques des paiements externes -->
                  <v-row class="mb-4" v-if="externalStats">
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                        <v-icon size="40" color="orange">mdi-cash-multiple</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('totalAmount') }}</h3>
                        <p class="text-subtitle-2 grey--text">💰 Total Dépensé</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('totalCount') }} transactions</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                        <v-icon size="40" color="green">mdi-check-circle</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('successCount') }}</h3>
                        <p class="text-subtitle-2 grey--text">✅ Réussies</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('successAmount') }} FCFA</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
                        <v-icon size="40" color="blue">mdi-clock</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('pendingCount') }}</h3>
                        <p class="text-subtitle-2 grey--text">⏳ En Attente</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('pendingAmount') }} FCFA</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
                        <v-icon size="40" color="purple">mdi-calendar</v-icon>
                        <h3 class="mt-2">{{ getLastExternalPaymentDate() }}</h3>
                        <p class="text-subtitle-2 grey--text">📅 Dernier Paiement</p>
                        <p class="text-caption grey--text">{{ getLastExternalPaymentAmount() }}</p>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Boutons de sélection rapide pour paiements externes -->
                  <v-row class="mb-3">
                    <v-col cols="12" class="text-center">
                      <v-btn-toggle v-model="selectedPeriodExternal" mandatory color="warning" dense
                                    class="flex-wrap justify-center">
                        <v-btn value="today" small>
                          <v-icon left small>mdi-calendar-today</v-icon>
                          Aujourd'hui
                        </v-btn>
                        <v-btn value="week" small>
                          <v-icon left small>mdi-calendar-week</v-icon>
                          Cette semaine
                        </v-btn>
                        <v-btn value="month" small>
                          <v-icon left small>mdi-calendar-month</v-icon>
                          Ce mois
                        </v-btn>
                        <v-btn value="last_month" small>
                          <v-icon left small>mdi-calendar-minus</v-icon>
                          Mois dernier
                        </v-btn>
                        <v-btn value="custom" small>
                          <v-icon left small>mdi-calendar-range</v-icon>
                          Personnalisé
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>

                  <!-- Sélecteur de plage personnalisée -->
                  <v-expand-transition>
                    <v-row v-show="selectedPeriodExternal === 'custom'" class="mb-3">
                      <v-col cols="12" sm="6">
                        <v-menu v-model="menuDateDebutExternal" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="formatDateFR(dateDebutExternal)" label="Date de début" prepend-icon="mdi-calendar"
                                          readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                          </template>
                          <v-date-picker v-model="dateDebutExternal" @input="menuDateDebutExternal = false"
                                         locale="fr"></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-menu v-model="menuDateFinExternal" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="formatDateFR(dateFinExternal)" label="Date de fin" prepend-icon="mdi-calendar"
                                          readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                          </template>
                          <v-date-picker v-model="dateFinExternal" @input="menuDateFinExternal = false"
                                         locale="fr"></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-expand-transition>

                  <!-- Filtres pour paiements externes -->
                  <v-row class="mb-4">
                    <v-col cols="12" sm="4">
                      <v-select v-model="selectedPaymentMethod" :items="paymentMethodOptions"
                                label="Méthode de paiement" prepend-icon="mdi-credit-card" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select v-model="selectedStatusExternal" :items="statusExternalOptions" label="Statut"
                                prepend-icon="mdi-check-circle" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="searchQueryExternal" label="Rechercher..." prepend-icon="mdi-magnify"
                                    outlined dense clearable @click:clear="clearSearchExternal"></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Tableau des paiements externes -->
                  <v-data-table :headers="headersExternal" :items="filteredExternalPayments" :items-per-page="10"
                                class="elevation-1" :loading="loadingExternal"
                                loading-text="Chargement des paiements externes..."
                                :no-data-text="getNoExternalDataText()">
                    <template v-slot:[`item.employee`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.employeeId)">
                          <span class="white--text text-caption font-weight-bold">{{
                              getInitials(item.employeeLastname, item.employeeFirstname)
                            }}</span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.employeeLastname }} {{ item.employeeFirstname }}</div>
                          <div class="text-caption grey--text">{{ item.employeeId }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:[`item.transactionDate`]="{ item }">{{
                        formatExternalDate(item.transactionDate)
                      }}
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                      <div class="d-flex align-center justify-end">
                        <span class="font-weight-bold text-h6 orange--text">{{
                            formatExternalAmount(item.amount)
                          }}</span>
                      </div>
                    </template>
                    <template v-slot:[`item.paymentMethod`]="{ item }">
                      <v-chip small :color="getPaymentMethodColor(item.paymentMethod, item)" dark>
                        <v-icon left small>{{ getPaymentMethodIcon(item.paymentMethod, item) }}</v-icon>
                        {{ item.paymentMethod }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                      <v-chip small :color="getExternalStatusColor(item.status)">
                        <v-icon left small>{{ getExternalStatusIcon(item.status) }}</v-icon>
                        {{ item.status }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.typeAuth`]="{ item }">
                      <v-chip x-small :color="getAuthColor(item.typeAuth)" outlined>{{ item.typeAuth }}</v-chip>
                    </template>
                    <template v-slot:[`item.customerType`]="{ item }">
                      <v-chip x-small :color="item.customerType === 'Identifié' ? 'success' : 'info'" outlined>
                        {{ item.customerType }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewDetailsExternal(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:no-data>
                      <div class="text-center pa-6">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-wallet-outline</v-icon>
                        <h3 class="grey--text text--lighten-1 mb-2">{{ getNoExternalDataTitle() }}</h3>
                        <p class="grey--text">{{ getNoExternalDataMessage() }}</p>
                        <v-btn v-if="!loadingExternal && externalPayments.length === 0" color="warning" outlined
                               @click="loadExternalPayments" class="mt-2">
                          <v-icon left>mdi-refresh</v-icon>
                          Actualiser
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- Tab Historique des Recharges (ENHANCED) -->
              <v-tab-item value="tab-recharges">
                <v-container fluid class="pa-4">
                  <v-alert color="success" text dense class="mb-4">
                    <v-icon left small>mdi-information</v-icon>
                    Consultez ici toutes les recharges effectuées par les employés de
                    <strong>{{ balanceInfos.onlyCompanyNameSelect }}</strong> dans ce restaurant.
                  </v-alert>

                  <!-- Enhanced Statistiques des recharges -->
                  <v-row class="mb-4" v-if="rechargeStats">
                    <v-col cols="12" sm="6">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                        <v-icon size="40" color="green">mdi-wallet-plus</v-icon>
                        <h3 class="mt-2">{{ getStatValue('totalCredits') }}</h3>
                        <p class="text-subtitle-2 grey--text">💰 Total Crédits</p>
                        <p class="text-caption grey--text">{{ getStatValue('countCredits') }} recharges</p>
                      </v-card>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                        <v-icon size="40" color="orange">mdi-history</v-icon>
                        <h3 class="mt-2">{{ getLastRechargeDate() }}</h3>
                        <p class="text-subtitle-2 grey--text">📅 Dernière Recharge</p>
                        <p class="text-caption grey--text">{{ getLastRechargeAmount() }}</p>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Boutons de sélection rapide pour recharges -->
                  <v-row class="mb-3">
                    <v-col cols="12" class="text-center">
                      <v-btn-toggle
                          v-model="selectedPeriodRecharges"
                          mandatory
                          color="success"
                          dense
                          class="flex-wrap justify-center"
                      >
                        <v-btn value="today" small>
                          <v-icon left small>mdi-calendar-today</v-icon>
                          Aujourd'hui
                        </v-btn>
                        <v-btn value="week" small>
                          <v-icon left small>mdi-calendar-week</v-icon>
                          Cette semaine
                        </v-btn>
                        <v-btn value="month" small>
                          <v-icon left small>mdi-calendar-month</v-icon>
                          Ce mois
                        </v-btn>
                        <v-btn value="last_month" small>
                          <v-icon left small>mdi-calendar-minus</v-icon>
                          Mois dernier
                        </v-btn>
                        <v-btn value="custom" small>
                          <v-icon left small>mdi-calendar-range</v-icon>
                          Personnalisé
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>

                  <!-- Sélecteur de plage personnalisée pour recharges -->
                  <v-expand-transition>
                    <v-row v-show="selectedPeriodRecharges === 'custom'" class="mb-3">
                      <v-col cols="12" sm="6">
                        <v-menu
                            v-model="menuDateDebutRecharges"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formatDateFR(dateDebutRecharges)"
                                label="Date de début"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                              v-model="dateDebutRecharges"
                              @input="menuDateDebutRecharges = false"
                              locale="fr"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-menu
                            v-model="menuDateFinRecharges"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formatDateFR(dateFinRecharges)"
                                label="Date de fin"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                              v-model="dateFinRecharges"
                              @input="menuDateFinRecharges = false"
                              locale="fr"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-expand-transition>

                  <!-- Filtres pour recharges -->
                  <v-row class="mb-4">
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedTypeRecharge"
                          :items="typeRechargeOptions"
                          label="Type d'opération"
                          prepend-icon="mdi-swap-vertical"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedStatusRecharge"
                          :items="statusRechargeOptions"
                          label="Statut"
                          prepend-icon="mdi-check-circle"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <!-- NEW: Payment Method Filter -->
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedPaymentMethodRecharge"
                          :items="paymentMethodRechargeOptions"
                          label="Méthode de paiement"
                          prepend-icon="mdi-credit-card"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                          v-model="searchQueryRecharges"
                          label="Rechercher..."
                          prepend-icon="mdi-magnify"
                          outlined
                          dense
                          clearable
                          @click:clear="clearSearchRecharges"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Enhanced Tableau des recharges -->
                  <v-data-table
                      :headers="headersRecharges"
                      :items="filteredRecharges"
                      :items-per-page="10"
                      class="elevation-1"
                      :loading="loadingRecharges"
                      loading-text="Chargement des recharges..."
                      :no-data-text="getNoDataText()"
                  >
                    <template v-slot:[`item.employee`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.employeeId)">
                          <span class="white--text text-caption font-weight-bold">
                            {{ getInitials(item.employeeLastname, item.employeeFirstname) }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.employeeLastname }} {{ item.employeeFirstname }}</div>
                          <div class="text-caption grey--text">{{ item.employeeId }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.dateTransaction`]="{ item }">
                      {{ formatDate(item.dateTransaction) }}
                    </template>

                    <template v-slot:[`item.amount`]="{ item }">
                      <div class="d-flex align-center justify-end">
                        <span :class="getAmountClass(item.typeOperation)">
                          {{ item.typeOperation === 'DEBIT' ? '-' : '+' }}{{ item.amount }} FCFA
                        </span>
                      </div>
                    </template>

                    <template v-slot:[`item.paymentMethod`]="{ item }">
                      <v-chip small :color="getPaymentMethodRechargeColor(item.paymentMethod)" dark>
                        <v-icon left small>{{ getPaymentMethodRechargeIcon(item.paymentMethod) }}</v-icon>
                        {{ item.paymentMethod }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.typeOperation`]="{ item }">
                      <v-chip small :color="getTypeOperationColor(item.typeOperation)" dark>
                        <v-icon left small>{{ getTypeOperationIcon(item.typeOperation) }}</v-icon>
                        {{ item.typeOperation }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.status`]="{ item }">
                      <v-chip small :color="getStatusColorRecharge(item.status)">
                        <v-icon left small>{{ getStatusIconRecharge(item.status) }}</v-icon>
                        {{ item.status }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.level`]="{ item }">
                      <v-chip small :color="getNiveauColor(item.level)" outlined>
                        {{ formatNiveau(item.level) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.typeAuth`]="{ item }">
                      <v-chip x-small :color="getAuthColor(item.typeAuth)" outlined>
                        {{ item.typeAuth }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewDetailsRecharge(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                      <v-btn
                          v-if="canCancelRecharge(item) && isValidStatusForCancellation(item.status)"
                          icon
                          small
                          color="warning"
                          @click="cancelRecharge(item)"
                          :disabled="!isValidStatusForCancellation(item.status)"
                      >
                        <v-icon small>mdi-cancel</v-icon>
                      </v-btn>
                    </template>

                    <!-- Enhanced custom no-data slot for better UX -->
                    <template v-slot:no-data>
                      <div class="text-center pa-6">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">
                          mdi-wallet-outline
                        </v-icon>
                        <h3 class="grey--text text--lighten-1 mb-2">
                          {{ getNoDataTitle() }}
                        </h3>
                        <p class="grey--text">
                          {{ getNoDataMessage() }}
                        </p>
                        <v-btn
                            v-if="!loadingRecharges && recharges.length === 0"
                            color="primary"
                            outlined
                            @click="loadRecharges"
                            class="mt-2"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Actualiser
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Dialog pour les details de transaction -->
    <v-dialog v-model="detailsDialog" max-width="350">
      <v-card>
        <v-card-title class="headline white--text" style="background-color: #03A9F4;">
          <v-icon left dark>mdi-receipt</v-icon>
          Details de la transaction
        </v-card-title>
        <v-card-text class="pt-4" v-if="selectedTransaction">
          <div v-html="transactionDetailsHtml"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les détails de recharge (ENHANCED) -->
    <v-dialog v-model="detailsRechargeDialog" max-width="500">
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-wallet-plus</v-icon>
          Détails de la recharge
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-html="detailsRechargeContent"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="detailsRechargeDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les détails de paiement externe -->
    <v-dialog v-model="detailsExternalDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-wallet-outline</v-icon>
          Détails du paiement externe
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-html="detailsExternalContent"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="warning" text @click="detailsExternalDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les alertes -->
    <v-dialog v-model="alertDialog" max-width="500">
      <v-card>
        <v-card-title :class="alertColor === 'success' ? 'success' : 'error'" class="white--text">
          {{ alertTitle }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ alertMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="alertDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";
import * as XLSX from "xlsx";
import moment from "moment";
import {depthDate} from "../../../helper/profondeur";
import {heightDate} from "../../../helper/hauteur";

export default {
  name: "RapportsRestaurantAdmin",
  data() {
    return {
      // Nouvelles propriétés à ajouter
      restaurants: [],
      companies: [],
      loadingRestaurants: false,
      loadingCompanies: false,
      selectedRestaurant: null,
      selectedCompany: null,

      externalPayments: [],
      externalStats: null,
      loadingExternal: false,

      // AJOUTER : Filtres pour paiements externes
      selectedPaymentMethod: "Tous",
      selectedStatusExternal: "Tous",
      selectedPaymentMethodRecharge: "Tous",
      searchQueryExternal: "",
      selectedPeriodExternal: "month",
      dateDebutExternal: moment().startOf('month').format('YYYY-MM-DD'),
      dateFinExternal: moment().endOf('month').format('YYYY-MM-DD'),

      // AJOUTER : Menus dates
      menuDateDebutExternal: false,
      menuDateFinExternal: false,

      // AJOUTER : Dialog
      detailsExternalDialog: false,
      detailsExternalContent: "",

      // AJOUTER : Options
      paymentMethodOptions: ["Tous", "Cash", "Wave", "Orange Money", "Autre"],
      statusExternalOptions: ["Tous", "SUCCESS", "PENDING", "FAILED", "CANCELLED"],

      // AJOUTER : Headers tableau
      headersExternal: [
        {text: "Employé", value: "employee", align: "start"},
        {text: "Date", value: "transactionDate", align: "start"},
        {text: "Montant", value: "amount", align: "end"},
        {text: "Méthode", value: "paymentMethod", align: "center"},
        {text: "Type Client", value: "customerType", align: "center"},
        {text: "Authentification", value: "typeAuth", align: "center"},
        {text: "Statut", value: "status", align: "center"},
        {text: "Description", value: "description"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ],

      // ENHANCED: Personal transactions stats
      personalTransactionsStats: {
        // Propriétés existantes
        currentMonth: 0,
        previousMonth: 0,
        difference: 0,
        percentageChange: 0,
        loading: false,
        error: null,

        // NOUVELLES PROPRIÉTÉS ENRICHIES pour la répartition wallet/external
        currentMonthWallet: 0,
        currentMonthExternal: 0,
        currentMonthTotal: 0,
        previousMonthWallet: 0,
        previousMonthExternal: 0,
        previousMonthTotal: 0,

        // Nouvelles propriétés pour l'analyse détaillée
        walletPercentage: 0,
        externalPercentage: 0,
        externalGrowthRate: 0,

        // Détails pour l'affichage enrichi
        breakdown: {
          wallet: {
            description: "Transactions via porte-monnaie personnel (solde épuisé)",
            currentAmount: 0,
            previousAmount: 0
          },
          external: {
            description: "Transactions externes 100% cash (paiement comptoir)",
            currentAmount: 0,
            previousAmount: 0
          }
        }
      },

      // ENHANCED: Unit price and financial calculations
      unitPriceTransaction: 0,
      sensNumerikInfos: {
        actualMonthEncours: "Chargement...",
        previousMonthEncours: "Chargement..."
      },

      // ENHANCED: Recharge functionality with better error handling
      activeTab: "tab-transactions",
      recharges: [],
      rechargeStats: null,
      loadingRecharges: false,

      // ENHANCED: Recharge filters with better defaults
      selectedTypeRecharge: "Tous",
      selectedStatusRecharge: "Tous",
      searchQueryRecharges: "",
      selectedPeriodRecharges: "month",
      dateDebutRecharges: moment().startOf('month').format('YYYY-MM-DD'),
      dateFinRecharges: moment().endOf('month').format('YYYY-MM-DD'),

      // ENHANCED: Date menu controls
      menuDateDebutRecharges: false,
      menuDateFinRecharges: false,

      // ENHANCED: Dialog management
      detailsRechargeDialog: false,
      detailsRechargeContent: "",

      // ENHANCED: Filter options with comprehensive choices
      typeRechargeOptions: ["Tous", "CREDIT", "DEBIT"],
      statusRechargeOptions: ["Tous", "SUCCESS", "FAILED", "CANCELLED", "PENDING"],
      paymentMethodRechargeOptions: ["Tous"],

      // ENHANCED: Table headers with proper structure
      headersRecharges: [
        {text: "Employé", value: "employee", align: "start"},
        {text: "Date", value: "dateTransaction", align: "start"},
        {text: "Montant", value: "amount", align: "end"},
        {text: "Type", value: "typeOperation", align: "center"},
        {text: "Méthode", value: "paymentMethod", align: "center"},
        {text: "Authentification", value: "typeAuth", align: "center"},
        {text: "Niveau", value: "level", align: "center"},
        {text: "Statut", value: "status", align: "center"},
        {text: "Raison", value: "reason"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ],

      loading: false,
      exportLoading: false,
      alertDialog: false,
      alertTitle: "",
      alertMessage: "",
      alertColor: "info",
      detailsDialog: false,
      selectedTransaction: null,
      restaurantFound: false,

      // Validation rules
      rules: {
        required: value => !!value || 'Ce champ est requis'
      },

      // Restaurant data
      transactions: [],
      searchName: "",

      // Date selects
      selectDates: depthDate,
      selectDatesHeight: heightDate,
      depthVal: {
        name: "Profondeur Aujourd'hui",
        value: "0"
      },
      heightVal: {
        name: "Hauteur Aujourd'hui",
        value: "0"
      },

      // Restaurant and company information
      balanceInfos: {
        matricule: "",
        companyName: "",
        companyId: "",
        restaurantName: "",
        restaurantId: "",
        globalRevenueActualMonth: 0,
        amountDueByCompanyActualMonth: 0,
        amountAlreadyPaidByEmployeeActualMonth: 0,
        globalRevenuePreviousMonth: 0,
        amountDueByCompanyPreviousMonth: 0,
        amountAlreadyPaidByEmployeePreviousMonth: 0,
        onlyCompaniesListFull: [],
        onlyCompaniesNamesList: [],
        onlyRestaurantsListFull: [],
        onlyRestaurantsNamesList: [],
        restaurantFullName: "", // NOUVEAU
        companyFullName: "",    // NOUVEAU
        onlyCompanyNameSelect: "", // Sélection automatique
        onlyRestaurantNameSelect: "", // Sélection automatique
      },

      financialDetails: {
        nctwWalletTransactionsActualMonthSite: 0,
        sumPriceTransactionsActualMonthSite: 0,
        sumPriceTransactionsActualMonthCompanySite: 0,
        sumPriceTransactionsActualMonthPersoSite: 0,
        nctwWalletTransactionsPreviousMonthSite: 0,
        sumPriceTransactionsPreviousMonthSite: 0,
        sumPriceTransactionsPreviousMonthCompanySite: 0,
        sumPriceTransactionsPreviousMonthPersoSite: 0,
        monthActual: "",
        monthPrevious: ""
      },

      // ENHANCED: Headers properly defined (CRITICAL FIX)
      headers: [
        {text: "Entreprise", value: "entreprise", align: "start"},
        {text: "Employe", value: "employe", align: "start"},
        {text: "Date Commande", value: "dateCommande", align: "center"},
        {text: "Montant", value: "montant", align: "end"},
        {text: "Type Operation", value: "typeOperation", align: "center"},
        {text: "Paiement", value: "paiement", align: "center"},
        {text: "Statut", value: "statut", align: "center"},
        {text: "Niveau", value: "niveau", align: "center"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ]
    };
  },

  mounted() {
    moment.locale('fr');
    // Les données seront chargées après sélection du restaurant
  },

  computed: {
    filteredExternalPayments() {
      let filtered = [...this.externalPayments];

      console.log("🔍 Starting external payments filtering with", filtered.length, "payments");

      if (this.dateDebutExternal && this.dateFinExternal) {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => {
          if (!p.transactionDate) return false;

          const transDate = this.parseExternalDate(p.transactionDate);
          if (!transDate || !transDate.isValid()) return false;

          const isInRange = transDate.isSameOrAfter(this.dateDebutExternal, 'day') &&
              transDate.isSameOrBefore(this.dateFinExternal, 'day');

          return isInRange;
        });
        console.log(`📅 External date filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.selectedPaymentMethod && this.selectedPaymentMethod !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => p.paymentMethod === this.selectedPaymentMethod);
        console.log(`💳 Payment method filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.selectedStatusExternal && this.selectedStatusExternal !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => p.status === this.selectedStatusExternal);
        console.log(`✅ Status filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.selectedPaymentMethodRecharge && this.selectedPaymentMethodRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => p.paymentMethod === this.selectedPaymentMethodRecharge);
        console.log(`Payment method filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.searchQueryExternal) {
        const beforeFilter = filtered.length;
        const query = this.searchQueryExternal.toLowerCase();
        filtered = filtered.filter(p =>
            (p.description && p.description.toLowerCase().includes(query)) ||
            (p.paymentMethod && p.paymentMethod.toLowerCase().includes(query)) ||
            p.amount.toString().includes(query) ||
            (p.transactionUid && p.transactionUid.toLowerCase().includes(query)) ||
            (p.customerName && p.customerName.toLowerCase().includes(query)) ||
            (p.employeeFirstname && p.employeeFirstname.toLowerCase().includes(query)) ||
            (p.employeeLastname && p.employeeLastname.toLowerCase().includes(query)) ||
            (p.employeeId && p.employeeId.toLowerCase().includes(query))
        );
        console.log(`🔍 Search filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      console.log("✅ Final filtered external payments:", filtered);
      return filtered;
    },

    // ENHANCED: Improved recharge filtering with better debugging
    filteredRecharges() {
      let filtered = [...this.recharges];

      console.log("🔍 Starting recharge filtering with", filtered.length, "recharges");

      if (this.dateDebutRecharges && this.dateFinRecharges) {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => {
          if (!r.dateTransaction) {
            console.log("⚠️ Recharge missing dateTransaction:", r);
            return false;
          }
          const transDate = moment(r.dateTransaction);
          const isValid = transDate.isValid();
          const isInRange = transDate.isSameOrAfter(this.dateDebutRecharges, 'day') &&
              transDate.isSameOrBefore(this.dateFinRecharges, 'day');

          if (!isValid) {
            console.log("❌ Invalid recharge date format:", r.dateTransaction, "for recharge:", r);
          }

          return isValid && isInRange;
        });
        console.log(`📅 Recharge date filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.selectedTypeRecharge && this.selectedTypeRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => r.typeOperation === this.selectedTypeRecharge);
        console.log(`🔄 Recharge type filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.selectedStatusRecharge && this.selectedStatusRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => r.status === this.selectedStatusRecharge);
        console.log(`✅ Recharge status filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      // NEW: Payment method filtering
      if (this.selectedPaymentMethodRecharge && this.selectedPaymentMethodRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => r.paymentMethod === this.selectedPaymentMethodRecharge);
        console.log(`Payment method filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.searchQueryRecharges) {
        const beforeFilter = filtered.length;
        const query = this.searchQueryRecharges.toLowerCase();
        filtered = filtered.filter(r =>
            (r.reason && r.reason.toLowerCase().includes(query)) ||
            (r.paymentMethod && r.paymentMethod.toLowerCase().includes(query)) ||
            r.amount.toString().includes(query) ||
            (r.transactionUid && r.transactionUid.toLowerCase().includes(query)) ||
            (r.employeeFirstname && r.employeeFirstname.toLowerCase().includes(query)) ||
            (r.employeeLastname && r.employeeLastname.toLowerCase().includes(query)) ||
            (r.employeeId && r.employeeId.toLowerCase().includes(query))
        );
        console.log(`🔍 Recharge search filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      console.log("✅ Final filtered recharges:", filtered);
      return filtered;
    },

    canSearch() {
      return this.selectedRestaurant !== null &&
          this.selectedRestaurant !== undefined &&
          this.selectedRestaurant !== "" &&
          this.selectedCompany !== null &&
          this.selectedCompany !== undefined &&
          this.selectedCompany !== "";
    },

    filteredTransactions() {
      let filtered = [...this.transactions];

      // Search filtering
      if (this.searchName && this.searchName.trim() !== '') {
        const query = this.searchName.toLowerCase().trim();
        filtered = filtered.filter(transaction => {
          return (
              // Search in company
              (transaction.entreprise && transaction.entreprise.toLowerCase().includes(query)) ||
              // Search in name
              (transaction.nom && transaction.nom.toLowerCase().includes(query)) ||
              // Search in first name
              (transaction.prenom && transaction.prenom.toLowerCase().includes(query)) ||
              // Search in full name
              (transaction.nom && transaction.prenom &&
                  `${transaction.nom} ${transaction.prenom}`.toLowerCase().includes(query)) ||
              // Search in matricule
              (transaction.matricule && transaction.matricule.toLowerCase().includes(query)) ||
              // Search in order date
              (transaction.dateCommande && transaction.dateCommande.toLowerCase().includes(query)) ||
              // Search in amount (convert to string)
              (transaction.montant && transaction.montant.toString().includes(query)) ||
              // Search in operation type
              (transaction.typeOperation && transaction.typeOperation.toLowerCase().includes(query)) ||
              // Search in payment method
              (transaction.paiement && transaction.paiement.toLowerCase().includes(query)) ||
              // Search in status
              (transaction.statut && transaction.statut.toLowerCase().includes(query)) ||
              // Search in additional information
              (transaction.infos && transaction.infos.toLowerCase().includes(query)) ||
              // Search in origin
              (transaction.origine && transaction.origine.toLowerCase().includes(query)) ||
              // Search in level (formatted)
              (transaction.niveau && this.formatNiveau(transaction.niveau).toLowerCase().includes(query))
          );
        });
      }

      return filtered;
    },

    transactionDetailsHtml() {
      if (!this.selectedTransaction) return "";

      const t = this.selectedTransaction;
      return `
        <strong style="color: #1976D2;">🏢 Entreprise</strong><br>
        ${t.entreprise}<br><br>

        <strong style="color: #1976D2;">👤 Employe</strong><br>
        ${t.nom} ${t.prenom} (${t.matricule})<br><br>

        <strong style="color: #1976D2;">📅 Date Commande</strong><br>
        ${this.convertDate(t.dateCommande)}<br><br>

        <strong style="color: #1976D2;">🕒 Date Transaction</strong><br>
        ${this.datefull(t.dateTransaction)}<br><br>

        <strong style="color: #1976D2;">💰 Montant</strong><br>
        <span style="font-size: 16px; font-weight: bold; color: #E65100;">${t.montant} FCFA</span><br><br>

        <strong style="color: #1976D2;">🔧 Type Operation</strong><br>
        ${t.typeOperation}<br><br>

        <strong style="color: #1976D2;">💳 Moyen de Paiement</strong><br>
        ${t.paiement}<br><br>

        <strong style="color: #1976D2;">✅ Statut</strong><br>
        <span style="color: ${this.getStatusColorHex(t.statut)}; font-weight: bold;">${t.statut}</span><br><br>

        <strong style="color: #1976D2;">📊 Niveau</strong><br>
        ${this.formatNiveau(t.niveau)}<br><br>

        <strong style="color: #1976D2;">🌐 Origine</strong><br>
        ${t.origine || 'N/A'}<br><br>

        ${t.infos && t.infos !== 'null' ? `<strong style="color: #1976D2;">ℹ️ Informations</strong><br>${t.infos}<br><br>` : ''}

        <hr style="margin: 15px 0;">
        <small style="color: #757575;">
          Transaction ID: ${t.transactionUid}<br>
          Reference: #${t.id}
        </small>
      `;
    }
  },

  watch: {
    selectedPeriodRecharges: {
      handler(newVal) {
        this.updateDateRangeRecharges(newVal);
        if (this.activeTab === 'tab-recharges') {
          // 🔧 SOLUTION RADICALE : Calcul dynamique seulement
          this.$nextTick(() => {
            this.calculateRechargeStatsFromTransactions();
          });
        }
      },
      immediate: false
    },

    filteredRecharges: {
      handler() {
        if (this.activeTab === 'tab-recharges') {
          // 🆕 CORRIGÉ : Délai plus long pour éviter conflit avec API
          setTimeout(() => {
            this.calculateRechargeStatsFromTransactions();
          }, 500);
        }
      },
      immediate: false
    },

    // ✅ UPDATED: activeTab watcher - loads data immediately if restaurant is found
    activeTab: {
      handler(newVal) {
        console.log("🔄 Active tab changed to:", newVal);

        // ✅ Only check if restaurant is found, don't wait for transactions
        if (!this.restaurantFound) {
          console.log("No restaurant found, skipping data load for tab:", newVal);
          return;
        }

        // ✅ Load data immediately for the selected tab
        switch (newVal) {
          case 'tab-recharges':
            console.log("📊 Loading recharges for admin view...");
            this.loadRecharges();
            break;
          case 'tab-paiements-externes':
            console.log("💳 Loading external payments for admin view...");
            this.loadExternalPayments();
            this.$nextTick(() => {
              this.calculateExternalStatsFromTransactions();
            });
            break;
          case 'tab-transactions':
            console.log("Transaction data should already be loading or loaded");
            // Transactions are loaded in background by loadData()
            break;
        }
      },
      immediate: false
    },

    // NOUVEAU : Watchers pour les paiements externes
    selectedPeriodExternal: {
      handler(newVal) {
        this.updateDateRangeExternal(newVal);
        if (this.activeTab === 'tab-paiements-externes' && this.restaurantFound) {
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }
      },
      immediate: false
    },

    // NOUVEAU : Watcher pour les paiements externes filtrés
    filteredExternalPayments: {
      handler() {
        if (this.activeTab === 'tab-paiements-externes') {
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }
      },
      immediate: false
    },
  },

  created() {
    this.balanceInfos.matricule = sessionStorage.getItem("userId");
    this.balanceInfos.companyName = sessionStorage.getItem("companyName");

    // Charger les données de façon asynchrone sans bloquer created()
    this.initializeData();

    // Initialize date ranges
    this.updateDateRangeRecharges(this.selectedPeriodRecharges);
    this.updateDateRangeExternal(this.selectedPeriodExternal);
  },

  methods: {
    async initializeData() {
      try {
        await Promise.all([
          this.loadRestaurants(),
          this.loadCompanies()
        ]);

        // Puis sélectionner les premiers éléments
        await this.selectFirstOptions();
      } catch (error) {
        console.error("❌ Erreur lors de l'initialisation:", error);

        // Fallback: essayer de charger depuis la session
        this.balanceInfos.onlyCompaniesListFull = JSON.parse(sessionStorage.getItem("onlyCompaniesListFull") || '[]');
        this.balanceInfos.onlyCompaniesNamesList = JSON.parse(sessionStorage.getItem("onlyCompaniesNamesList") || '[]');
        this.balanceInfos.onlyRestaurantsListFull = JSON.parse(sessionStorage.getItem("onlyRestaurantsListFull") || '[]');
        this.balanceInfos.onlyRestaurantsNamesList = JSON.parse(sessionStorage.getItem("onlyRestaurantsNamesList") || '[]');
      }
    },

    calculateRechargeStatsFromTransactions() {
      console.log("=== DEBUGGING CALCUL STATS RECHARGES (ADMIN) ===");
      console.log("📅 Période actuelle:", this.selectedPeriodRecharges);
      console.log("📅 Date début:", this.dateDebutRecharges);
      console.log("📅 Date fin:", this.dateFinRecharges);
      console.log("📊 Recharges brutes:", this.recharges.length);

      // 🔧 CORRIGÉ : Calculer sur données brutes avec SEULEMENT filtre de dates
      const rechargesToAnalyze = this.recharges.filter(r => {
        if (!r.dateTransaction) return false;

        const transDate = moment(r.dateTransaction);
        if (!transDate.isValid()) return false;

        return transDate.isSameOrAfter(this.dateDebutRecharges, 'day') &&
            transDate.isSameOrBefore(this.dateFinRecharges, 'day');
      });

      console.log("📊 Recharges filtrées (dates seulement):", rechargesToAnalyze.length);

      // 🆕 VALIDATION : Vérifier cohérence API vs Calcul dynamique
      if (this.rechargeStats && rechargesToAnalyze.length > 0) {
        const dynamicTotal = rechargesToAnalyze
            .filter(r => r.typeOperation === 'CREDIT')
            .reduce((sum, r) => sum + (parseInt(r.amount) || 0), 0);

        console.log("💰 Comparaison totaux:");
        console.log("  - API synchronisé:", this.rechargeStats.totalCredits || 0);
        console.log("  - Calcul dynamique:", dynamicTotal);

        const difference = Math.abs((this.rechargeStats.totalCredits || 0) - dynamicTotal);
        if (difference > 0) {
          console.warn("⚠️ DIFFÉRENCE détectée:", difference, "FCFA");
          console.log("Possible cause: Délai de synchronisation ou filtres différents");
        } else {
          console.log("✅ Totaux cohérents - Synchronisation réussie");
        }
      }

      if (!rechargesToAnalyze || rechargesToAnalyze.length === 0) {
        this.rechargeStats = {
          totalCredits: 0,
          countCredits: 0,
          totalDebits: 0,
          countDebits: 0,
          lastRecharge: null
        };
        console.log("📊 Stats vides - Aucune recharge filtrée");
        return;
      }

      const stats = {
        totalCredits: 0,
        countCredits: 0,
        totalDebits: 0,
        countDebits: 0,
        lastRecharge: null
      };

      const sortedRecharges = [...rechargesToAnalyze].sort((a, b) =>
          moment(b.dateTransaction).valueOf() - moment(a.dateTransaction).valueOf()
      );

      if (sortedRecharges.length > 0) {
        stats.lastRecharge = {
          date: sortedRecharges[0].dateTransaction,
          amount: sortedRecharges[0].amount
        };
      }

      // Détail des recharges filtrées pour debugging
      console.log("📋 Détail des recharges filtrées:");
      rechargesToAnalyze.forEach((r, index) => {
        console.log(`${index + 1}. ${r.dateTransaction} - ${r.typeOperation} - ${r.amount} FCFA`);
      });

      rechargesToAnalyze.forEach(recharge => {
        const amount = parseInt(recharge.amount) || 0;

        if (recharge.typeOperation === 'CREDIT') {
          stats.totalCredits += amount;
          stats.countCredits++;
        } else if (recharge.typeOperation === 'DEBIT') {
          stats.totalDebits += amount;
          stats.countDebits++;
        }
      });

      // 🔧 CORRIGÉ : Mettre à jour rechargeStats avec le calcul dynamique
      this.rechargeStats = {
        ...this.rechargeStats,  // Garder les autres propriétés de l'API
        totalCredits: stats.totalCredits,
        countCredits: stats.countCredits,
        totalDebits: stats.totalDebits,
        countDebits: stats.countDebits,
        lastRecharge: stats.lastRecharge
      };

      console.log("📊 Stats calculées (comparaison seulement):", stats);
      console.log("📊 Stats API (utilisées pour affichage):", this.rechargeStats);
      console.log("===================================");
    },

    formatDateFR(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    },

    calculateExternalStatsFromTransactions() {
      // MODIFICATION : Utiliser les paiements filtrés au lieu de tous les paiements
      const paymentsToAnalyze = this.filteredExternalPayments || [];

      if (!paymentsToAnalyze || paymentsToAnalyze.length === 0) {
        this.externalStats = {
          totalAmount: 0,
          totalCount: 0,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };
        return;
      }

      const stats = {
        totalAmount: 0,
        totalCount: paymentsToAnalyze.length,
        successCount: 0,
        successAmount: 0,
        pendingCount: 0,
        pendingAmount: 0,
        failedCount: 0,
        failedAmount: 0,
        lastPayment: null
      };

      const sortedPayments = [...paymentsToAnalyze].sort((a, b) =>
          moment(b.transactionDate).valueOf() - moment(a.transactionDate).valueOf()
      );

      if (sortedPayments.length > 0) {
        stats.lastPayment = {
          date: sortedPayments[0].transactionDate,
          amount: sortedPayments[0].amount
        };
      }

      paymentsToAnalyze.forEach(payment => {
        const amount = parseInt(payment.amount) || 0;
        stats.totalAmount += amount;

        switch (payment.status) {
          case 'SUCCESS':
            stats.successCount++;
            stats.successAmount += amount;
            break;
          case 'PENDING':
            stats.pendingCount++;
            stats.pendingAmount += amount;
            break;
          case 'FAILED':
          case 'CANCELLED':
            stats.failedCount++;
            stats.failedAmount += amount;
            break;
        }
      });

      this.externalStats = stats;
      console.log("📊 Stats externes mises à jour:", this.externalStats);
    },

    // Charger la liste des entreprises depuis l'API
    async loadCompanies() {
      this.loadingCompanies = true;

      try {
        const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_only_companies/${sessionStorage.getItem("accountId")}`;

        console.log("🏢 Chargement des entreprises depuis:", url);

        const response = await apiClient.get(url);

        if (response.data && response.data.status === 200) {
          if (response.data.datas && Array.isArray(response.data.datas) && response.data.datas.length > 0) {

            // Filtrer pour ne garder que les entreprises (restaurant: false) et actives
            const companiesData = response.data.datas.filter(item =>
                item.restaurant === false && item.disabled === false
            );

            this.companies = companiesData.map(company => ({
              id: company.id,
              name: company.name,
              address: company.address || '',
              phone: company.phone || '',
              sigle: company.sigle || '',
              label: company.name + (company.sigle ? ` (${company.sigle})` : ''),
              disabled: company.disabled,
              creationDate: company.creationDate
            }));

            console.log("✅ Entreprises chargées:", this.companies.length);
            console.log("📋 Première entreprise:", this.companies[0]);

            // Mettre à jour les données de session pour compatibilité
            this.updateSessionCompaniesData();

          } else {
            console.warn("⚠️ Aucune entreprise trouvée dans la réponse");
            this.companies = [];
          }
        } else {
          console.error("❌ Réponse invalide:", response.data);
          this.companies = [];
        }
      } catch (error) {
        console.error("❌ Erreur lors du chargement des entreprises:", error);
        this.companies = [];
        this.showAlert("error", "Erreur", "Impossible de charger la liste des entreprises");
      } finally {
        this.loadingCompanies = false;
      }
    },

// Charger la liste des restaurants depuis l'API
    async loadRestaurants() {
      this.loadingRestaurants = true;

      try {
        const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_only_restaurants/${sessionStorage.getItem("accountId")}`;

        console.log("🍽️ Chargement des restaurants depuis:", url);

        const response = await apiClient.get(url);

        if (response.data && response.data.status === 200) {
          if (response.data.datas && Array.isArray(response.data.datas) && response.data.datas.length > 0) {

            // Filtrer pour ne garder que les restaurants (restaurant: true) et actifs
            const restaurantsData = response.data.datas.filter(item =>
                item.restaurant === true && item.disabled === false
            );

            this.restaurants = restaurantsData.map(restaurant => ({
              id: restaurant.id,
              name: restaurant.name,
              address: restaurant.address || '',
              phone: restaurant.phone || '',
              sigle: restaurant.sigle || '',
              label: restaurant.name + (restaurant.address ? ` - ${restaurant.address}` : ''),
              disabled: restaurant.disabled,
              creationDate: restaurant.creationDate
            }));

            console.log("✅ Restaurants chargés:", this.restaurants.length);
            console.log("🍽️ Premier restaurant:", this.restaurants[0]);

            // Mettre à jour les données de session pour compatibilité
            this.updateSessionRestaurantsData();

          } else {
            console.warn("⚠️ Aucun restaurant trouvé dans la réponse");
            this.restaurants = [];
          }
        } else {
          console.error("❌ Réponse invalide:", response.data);
          this.restaurants = [];
        }
      } catch (error) {
        console.error("❌ Erreur lors du chargement des restaurants:", error);
        this.restaurants = [];
        this.showAlert("error", "Erreur", "Impossible de charger la liste des restaurants");
      } finally {
        this.loadingRestaurants = false;
      }
    },

// Mettre à jour les données de session pour les entreprises (pour compatibilité)
    updateSessionCompaniesData() {
      try {
        const companiesNamesList = this.companies.map(c => c.name);
        const companiesListFull = [];

        this.companies.forEach(company => {
          companiesListFull.push(company.id.toString());
          companiesListFull.push(company.name);
        });

        this.balanceInfos.onlyCompaniesNamesList = companiesNamesList;
        this.balanceInfos.onlyCompaniesListFull = companiesListFull;

        // Sauvegarder en session pour compatibilité avec le code existant
        sessionStorage.setItem("onlyCompaniesNamesList", JSON.stringify(companiesNamesList));
        sessionStorage.setItem("onlyCompaniesListFull", JSON.stringify(companiesListFull));

        console.log("💾 Données entreprises mises à jour en session");
      } catch (error) {
        console.error("❌ Erreur mise à jour session entreprises:", error);
      }
    },

// Mettre à jour les données de session pour les restaurants (pour compatibilité)
    updateSessionRestaurantsData() {
      try {
        const restaurantsNamesList = this.restaurants.map(r => r.name);
        const restaurantsListFull = [];

        this.restaurants.forEach(restaurant => {
          restaurantsListFull.push(restaurant.id.toString());
          restaurantsListFull.push(restaurant.name);
        });

        this.balanceInfos.onlyRestaurantsNamesList = restaurantsNamesList;
        this.balanceInfos.onlyRestaurantsListFull = restaurantsListFull;

        // Sauvegarder en session pour compatibilité avec le code existant
        sessionStorage.setItem("onlyRestaurantsNamesList", JSON.stringify(restaurantsNamesList));
        sessionStorage.setItem("onlyRestaurantsListFull", JSON.stringify(restaurantsListFull));

        console.log("💾 Données restaurants mises à jour en session");
      } catch (error) {
        console.error("❌ Erreur mise à jour session restaurants:", error);
      }
    },

// MODIFIER la méthode updateRestaurantSelection
    updateRestaurantSelection(selectedName) {
      console.log("🍽️ Restaurant sélectionné:", selectedName);

      if (selectedName) {
        const restaurant = this.restaurants.find(r => r.name === selectedName);

        if (restaurant) {
          this.balanceInfos.onlyRestaurantNameSelect = restaurant.name;
          this.balanceInfos.restaurantFullName = restaurant.name;
          this.balanceInfos.restaurantId = restaurant.id; // IMPORTANT: Stocker l'ID

          console.log("✅ Données restaurant mises à jour:", {
            nom: this.balanceInfos.onlyRestaurantNameSelect,
            nomComplet: this.balanceInfos.restaurantFullName,
            id: this.balanceInfos.restaurantId
          });
        }
      } else {
        this.balanceInfos.onlyRestaurantNameSelect = "";
        this.balanceInfos.restaurantFullName = "";
        this.balanceInfos.restaurantId = null;
      }
    },

// MODIFIER la méthode updateCompanySelection
    updateCompanySelection(selectedName) {
      console.log("🏢 Entreprise sélectionnée:", selectedName);

      if (selectedName) {
        const company = this.companies.find(c => c.name === selectedName);

        if (company) {
          this.balanceInfos.onlyCompanyNameSelect = company.name;
          this.balanceInfos.companyFullName = company.name;
          this.balanceInfos.companyId = company.id; // IMPORTANT: Stocker l'ID

          console.log("✅ Données entreprise mises à jour:", {
            nom: this.balanceInfos.onlyCompanyNameSelect,
            nomComplet: this.balanceInfos.companyFullName,
            id: this.balanceInfos.companyId
          });
        }
      } else {
        this.balanceInfos.onlyCompanyNameSelect = "";
        this.balanceInfos.companyFullName = "";
        this.balanceInfos.companyId = null;
      }
    },

// Obtenir le nom complet du restaurant pour affichage
    getRestaurantDisplayName() {
      return this.balanceInfos.restaurantFullName || this.balanceInfos.onlyRestaurantNameSelect || "";
    },

// Obtenir le nom complet de l'entreprise pour affichage
    getCompanyDisplayName() {
      return this.balanceInfos.companyFullName || this.balanceInfos.onlyCompanyNameSelect || "";
    },

// MODIFIER la méthode selectFirstOptions
    async selectFirstOptions() {
      try {
        // Attendre que les données soient chargées
        await Promise.all([
          this.restaurants.length === 0 ? this.loadRestaurants() : Promise.resolve(),
          this.companies.length === 0 ? this.loadCompanies() : Promise.resolve()
        ]);

        // Sélectionner le premier restaurant
        if (this.restaurants && this.restaurants.length > 0) {
          this.selectedRestaurant = this.restaurants[0].name;
          this.updateRestaurantSelection(this.selectedRestaurant);
          console.log("🎯 Premier restaurant sélectionné:", this.selectedRestaurant);
        }

        // Sélectionner la première entreprise
        if (this.companies && this.companies.length > 0) {
          this.selectedCompany = this.companies[0].name;
          this.updateCompanySelection(this.selectedCompany);
          console.log("🎯 Première entreprise sélectionnée:", this.selectedCompany);
        }

      } catch (error) {
        console.error("❌ Erreur lors de la sélection automatique:", error);
      }
    },

    async loadExternalPayments() {
      // ✅ Check for required data instead of transactions.length
      if (!this.balanceInfos.restaurantId || !this.balanceInfos.companyId) {
        console.log("Missing required data for loading external payments:", {
          restaurantId: this.balanceInfos.restaurantId,
          companyId: this.balanceInfos.companyId
        });
        this.showAlert("warning", "Données manquantes", "Informations du restaurant ou de l'entreprise non disponibles");
        return;
      }

      console.log("🚀 Loading external payments for restaurant admin view...");
      console.log("Restaurant ID:", this.balanceInfos.restaurantId);
      console.log("Company ID:", this.balanceInfos.companyId);

      this.loadingExternal = true;

      try {
        await Promise.allSettled([
          this.getExternalPayments(),
          this.getExternalPaymentStatistics()
        ]);

        console.log("✅ External payments loaded successfully");

        if (this.externalPayments.length === 0) {
          console.log("ℹ️ No external payment history found for this company/restaurant combination");
          this.showAlert("info", "Information",
              "Aucun historique de paiement externe trouvé pour cette combinaison restaurant/entreprise.");
        }

      } catch (error) {
        console.error("❌ Error during external payments loading:", error);
        this.showAlert("error", "Erreur", "Impossible de charger l'historique des paiements externes");
      } finally {
        this.loadingExternal = false;
        console.log("🏁 External payments loading finished");
      }
    },

    // AJOUTER : Get external payments for all employees
    async getExternalPayments() {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");

      try {
        // ✅ NEW APPROACH: Use company-wide endpoint for all employees
        const url = `${this.$store.getters.user.baseUrl}/api/external-payments/history/company/${this.balanceInfos.companyId}`;

        console.log("📞 Fetching external payments for entire company:", this.balanceInfos.companyId);

        const response = await apiClient.get(url, {
          params: {
            startDate: startDate,
            endDate: endDate,
            restaurantId: this.balanceInfos.restaurantId, // Optional filter
          }
        });

        console.log("📊 Company external payments response:", response.data);

        if (response.data && Array.isArray(response.data)) {
          this.externalPayments = response.data.map(item => ({
            id: item.id,
            transactionUid: item.transactionUid,
            transactionDate: item.transactionDate,
            amount: item.amount,
            currency: item.currency || 'XOF',
            paymentMethod: item.paymentMethod ? item.paymentMethod.name : 'N/A',
            status: item.status,
            typeAuth: item.typeAuth || 'N/A',
            description: item.description || 'N/A',
            externalReference: item.externalReference || '',
            customerType: item.account ? 'Identifié' : 'Anonyme',
            customerName: item.account ?
                item.displayCustomerName || `${item.account.accountFirstname} ${item.account.accountLastname}` :
                item.anonymousCustomerName || 'Client anonyme',
            customerPhone: item.account ?
                item.account.accountPhonenumber || 'N/A' :
                item.anonymousCustomerPhone || 'N/A',
            customerEmail: item.account ?
                item.displayCustomerEmail || item.account.accountEmail :
                item.anonymousCustomerEmail || 'N/A',
            origin: item.origin || 'N/A',
            processedBy: item.processedBy || 'N/A',
            notes: item.notes || '',
            failureReason: item.failureReason || '',
            creationDate: item.creationDate,
            revisedDate: item.revisedDate,
            paymentMethodColor: item.paymentMethod ? item.paymentMethod.color : null,
            paymentMethodIcon: item.paymentMethod ? item.paymentMethod.icon : null,
            paymentMethodDescription: item.paymentMethod ? item.paymentMethod.description : null,
            isAnonymous: item.anonymousTransaction || false,
            accountInfo: item.account || null,
            // Employee info from the response
            employeeId: item.employee?.userId || item.employeeId || 'N/A',
            employeeFirstname: item.employee?.accountFirstname || item.employeeFirstname || 'N/A',
            employeeLastname: item.employee?.accountLastname || item.employeeLastname || 'N/A'
          }));

          // Extract unique payment methods
          const paymentMethods = [...new Set(this.externalPayments.map(p => p.paymentMethod))];
          this.paymentMethodOptions = ["Tous", ...paymentMethods];

          console.log("✅ Total external payments loaded:", this.externalPayments.length);
        } else {
          console.log("⚠️ No external payments data returned");
          this.externalPayments = [];
        }

      } catch (error) {
        console.error("❌ Error loading company external payments:", error);

        // ✅ FALLBACK: If company endpoint doesn't exist, try individual employee approach
        console.log("🔄 Trying external payments fallback approach...");
        await this.getExternalPaymentsFallback();
      }
    },

// ✅ NEW METHOD: Fallback approach for external payments
    async getExternalPaymentsFallback() {
      try {
        // If we have transactions, use employee IDs from there
        if (this.transactions && this.transactions.length > 0) {
          const employeeIds = [...new Set(this.transactions.map(t => t.matricule))];
          await this.getExternalPaymentsForEmployees(employeeIds);
        } else {
          // If no transactions yet, load for all company employees
          await this.getExternalPaymentsForAllCompanyEmployees();
        }
      } catch (error) {
        console.error("❌ Error in external payments fallback:", error);
        this.externalPayments = [];
      }
    },

// ✅ NEW METHOD: Get external payments for all company employees
    async getExternalPaymentsForAllCompanyEmployees() {
      try {
        // Get all employees for this company
        const employeesUrl = `${this.$store.getters.user.baseUrl}/account/users/company/${this.balanceInfos.companyId}`;
        const employeesResponse = await apiClient.get(employeesUrl);

        if (employeesResponse.data && employeesResponse.data.status === 200 && employeesResponse.data.datas) {
          const employeeIds = employeesResponse.data.datas.map(emp => emp.userId);
          console.log("👥 Found", employeeIds.length, "company employees for external payments");
          await this.getExternalPaymentsForEmployees(employeeIds);
        } else {
          console.log("⚠️ No employees found for company");
          this.externalPayments = [];
        }
      } catch (error) {
        console.error("❌ Error getting company employees for external payments:", error);
        this.externalPayments = [];
      }
    },

// ✅ NEW METHOD: Get external payments for specific employees
    async getExternalPaymentsForEmployees(employeeIds) {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");
      const allExternalPayments = [];

      for (const matricule of employeeIds) {
        try {
          const url = `${this.$store.getters.user.baseUrl}/api/external-payments/history/employee/${matricule}/${this.balanceInfos.companyId}`;

          const response = await apiClient.get(url, {
            params: {startDate, endDate}
          });

          if (response.data && Array.isArray(response.data)) {
            const employeeExternalPayments = response.data.map(item => ({
              id: item.id,
              transactionUid: item.transactionUid,
              transactionDate: item.transactionDate,
              amount: item.amount,
              currency: item.currency || 'XOF',
              paymentMethod: item.paymentMethod ? item.paymentMethod.name : 'N/A',
              status: item.status,
              typeAuth: item.typeAuth || 'N/A',
              description: item.description || 'N/A',
              externalReference: item.externalReference || '',
              customerType: item.account ? 'Identifié' : 'Anonyme',
              customerName: item.account ?
                  item.displayCustomerName || `${item.account.accountFirstname} ${item.account.accountLastname}` :
                  item.anonymousCustomerName || 'Client anonyme',
              customerPhone: item.account ?
                  item.account.accountPhonenumber || 'N/A' :
                  item.anonymousCustomerPhone || 'N/A',
              customerEmail: item.account ?
                  item.displayCustomerEmail || item.account.accountEmail :
                  item.anonymousCustomerEmail || 'N/A',
              origin: item.origin || 'N/A',
              processedBy: item.processedBy || 'N/A',
              notes: item.notes || '',
              failureReason: item.failureReason || '',
              creationDate: item.creationDate,
              revisedDate: item.revisedDate,
              paymentMethodColor: item.paymentMethod ? item.paymentMethod.color : null,
              paymentMethodIcon: item.paymentMethod ? item.paymentMethod.icon : null,
              paymentMethodDescription: item.paymentMethod ? item.paymentMethod.description : null,
              isAnonymous: item.anonymousTransaction || false,
              accountInfo: item.account || null,
              // Add employee info
              employeeId: matricule,
              employeeFirstname: this.getEmployeeFirstname(matricule),
              employeeLastname: this.getEmployeeLastname(matricule)
            }));

            allExternalPayments.push(...employeeExternalPayments);
          }
        } catch (employeeError) {
          console.warn(`⚠️ Error for employee ${matricule}:`, employeeError);
          // Continue with other employees
        }
      }

      this.externalPayments = allExternalPayments;

      // Extract unique payment methods
      const paymentMethods = [...new Set(this.externalPayments.map(p => p.paymentMethod))];
      this.paymentMethodOptions = ["Tous", ...paymentMethods];

      console.log("📋 Total external payments loaded:", this.externalPayments.length);
    },

    // AJOUTER : Get external payment statistics
    async getExternalPaymentStatistics() {
      try {
        if (this.externalPayments.length === 0) {
          this.externalStats = {
            totalAmount: 0,
            totalCount: 0,
            successCount: 0,
            successAmount: 0,
            pendingCount: 0,
            pendingAmount: 0,
            failedCount: 0,
            failedAmount: 0,
            lastPayment: null
          };
          return;
        }

        const stats = {
          totalAmount: 0,
          totalCount: this.externalPayments.length,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };

        // Sort by date to find last payment
        const sortedPayments = [...this.externalPayments].sort((a, b) =>
            moment(b.transactionDate).valueOf() - moment(a.transactionDate).valueOf()
        );

        if (sortedPayments.length > 0) {
          stats.lastPayment = {
            date: sortedPayments[0].transactionDate,
            amount: sortedPayments[0].amount
          };
        }

        // Calculate statistics by status
        this.externalPayments.forEach(payment => {
          const amount = parseInt(payment.amount) || 0;
          stats.totalAmount += amount;

          switch (payment.status) {
            case 'SUCCESS':
              stats.successCount++;
              stats.successAmount += amount;
              break;
            case 'PENDING':
              stats.pendingCount++;
              stats.pendingAmount += amount;
              break;
            case 'FAILED':
            case 'CANCELLED':
              stats.failedCount++;
              stats.failedAmount += amount;
              break;
          }
        });

        this.externalStats = stats;
        console.log("📊 External payment statistics calculated:", this.externalStats);

      } catch (error) {
        console.error("❌ Error calculating external payment statistics:", error);
        this.externalStats = {
          totalAmount: 0,
          totalCount: 0,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };
      }
    },

    // AJOUTER : Update date range for external payments
    updateDateRangeExternal(period) {
      switch (period) {
        case 'today':
          this.dateDebutExternal = moment().format('YYYY-MM-DD');
          this.dateFinExternal = moment().format('YYYY-MM-DD');
          break;
        case 'week':
          this.dateDebutExternal = moment().startOf('week').format('YYYY-MM-DD');
          this.dateFinExternal = moment().endOf('week').format('YYYY-MM-DD');
          break;
        case 'month':
          this.dateDebutExternal = moment().startOf('month').format('YYYY-MM-DD');
          this.dateFinExternal = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'last_month':
          this.dateDebutExternal = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.dateFinExternal = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
      }
    },

    // AJOUTER : External payment utility methods
    formatExternalDate(date) {
      if (!date || date === "0") return "N/A";

      let parsedDate;
      if (typeof date === 'string' && date.includes(' ')) {
        parsedDate = moment(date, "YYYY-MM-DD HH:mm:ss");
      } else {
        parsedDate = moment(date);
      }

      return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY HH:mm") : "Date invalide";
    },

    formatExternalAmount(amount) {
      if (!amount || amount === 0) return "0 FCFA";
      return new Intl.NumberFormat('fr-FR').format(amount) + " FCFA";
    },

    parseExternalDate(date) {
      if (!date) return null;

      if (typeof date === 'string' && date.includes(' ')) {
        return moment(date, "YYYY-MM-DD HH:mm:ss");
      } else {
        return moment(date);
      }
    },

    getPaymentMethodColor(method, item = null) {
      if (item && item.paymentMethodColor) {
        return item.paymentMethodColor;
      }

      const colors = {
        "Cash": "green",
        "Wave": "blue",
        "Orange Money": "orange",
        "Carte Bancaire": "purple",
        "Espèces": "green",
        "Autre": "grey"
      };
      return colors[method] || "grey";
    },

    getPaymentMethodIcon(method, item = null) {
      if (item && item.paymentMethodIcon) {
        return `mdi-${item.paymentMethodIcon}`;
      }

      const icons = {
        "Cash": "mdi-cash",
        "Wave": "mdi-water",
        "Orange Money": "mdi-phone",
        "Carte Bancaire": "mdi-credit-card",
        "Espèces": "mdi-cash",
        "Autre": "mdi-wallet"
      };
      return icons[method] || "mdi-wallet";
    },

    getExternalStatusColor(status) {
      const colors = {
        "SUCCESS": "success",
        "FAILED": "error",
        "CANCELLED": "warning",
        "PENDING": "info"
      };
      return colors[status] || "grey";
    },

    getExternalStatusIcon(status) {
      const icons = {
        "SUCCESS": "mdi-check",
        "FAILED": "mdi-close",
        "CANCELLED": "mdi-cancel",
        "PENDING": "mdi-clock"
      };
      return icons[status] || "mdi-help";
    },

    getExternalStatValue(statKey) {
      if (!this.externalStats) return "0";

      const value = this.externalStats[statKey];
      if (value === null || value === undefined) return "0";

      if (statKey.includes('Amount')) {
        return this.formatNumber(value);
      }

      return value.toString();
    },

    getLastExternalPaymentDate() {
      if (!this.externalStats || !this.externalStats.lastPayment) {
        return "Aucun";
      }
      return this.formatExternalDate(this.externalStats.lastPayment.date);
    },

    getLastExternalPaymentAmount() {
      if (!this.externalStats || !this.externalStats.lastPayment) {
        return "Aucun paiement effectué";
      }
      return `${this.externalStats.lastPayment.amount} FCFA`;
    },

    viewDetailsExternal(item) {
      const details = `
      <strong style="color: #FF9800;">👤 Employé</strong><br>
      ${item.employeeLastname} ${item.employeeFirstname} (${item.employeeId})<br><br>

      <strong style="color: #FF9800;">💰 Montant</strong><br>
      <span style="font-size: 18px; font-weight: bold; color: #FF9800;">
        ${this.formatExternalAmount(item.amount)}
      </span><br><br>

      <strong style="color: #FF9800;">📅 Date et Heure</strong><br>
      ${this.formatExternalDate(item.transactionDate)}<br><br>

      <strong style="color: #FF9800;">💳 Méthode de Paiement</strong><br>
      ${item.paymentMethod}<br><br>

      <strong style="color: #FF9800;">👤 Type de Client</strong><br>
      ${item.customerType}<br><br>

      <strong style="color: #FF9800;">👥 Client</strong><br>
      ${item.customerName}<br>
      ${item.customerPhone || 'N/A'}<br><br>

      <strong style="color: #FF9800;">🔐 Authentification</strong><br>
      ${item.typeAuth}<br><br>

      <strong style="color: #FF9800;">✅ Statut</strong><br>
      ${item.status}<br><br>

      <strong style="color: #FF9800;">📝 Description</strong><br>
      ${item.description}<br><br>

      ${item.externalReference ? `<strong style="color: #FF9800;">🔗 Référence Externe</strong><br>${item.externalReference}<br><br>` : ''}

      ${item.failureReason ? `<strong style="color: #F44336;">❌ Raison d'Échec</strong><br>${item.failureReason}<br><br>` : ''}

      ${item.notes ? `<strong style="color: #FF9800;">📋 Notes</strong><br>${item.notes}<br><br>` : ''}

      <hr style="margin: 15px 0;">
      <small style="color: #757575;">
        Transaction ID: ${item.transactionUid}<br>
        Référence: #${item.id}<br>
        Restaurant: ${this.balanceInfos.onlyRestaurantNameSelect}<br>
        ${item.processedBy ? `Traité par: ${item.processedBy}` : ''}
      </small>
    `;

      this.detailsExternalDialog = true;
      this.detailsExternalContent = details;
    },

    clearSearchExternal() {
      this.searchQueryExternal = "";
    },

    getNoExternalDataText() {
      if (this.loadingExternal) {
        return "Chargement des paiements externes...";
      }
      if (this.externalPayments.length === 0) {
        return "Aucun paiement externe trouvé";
      }
      return "Aucun paiement externe correspond aux filtres sélectionnés";
    },

    getNoExternalDataTitle() {
      if (this.externalPayments.length === 0) {
        return "Aucun historique de paiement externe";
      }
      return "Aucun résultat";
    },

    getNoExternalDataMessage() {
      if (this.externalPayments.length === 0) {
        return "Aucun paiement externe n'a été effectué par les employés de cette entreprise pour le moment.";
      }
      return "Essayez de modifier vos filtres ou votre période de recherche.";
    },

    getNoDataMessage() {
      if (this.recharges.length === 0) {
        return "Aucune recharge n'a été effectuée par les employés de cette entreprise pour le moment.";
      }
      return "Essayez de modifier vos filtres ou votre période de recherche.";
    },

    getLastRechargeDate() {
      if (!this.rechargeStats || !this.rechargeStats.lastRecharge) {
        return "Aucune";
      }
      return this.formatDate(this.rechargeStats.lastRecharge.date);
    },

    getLastRechargeAmount() {
      if (!this.rechargeStats || !this.rechargeStats.lastRecharge) {
        return "Aucune recharge effectuée";
      }
      return `${this.rechargeStats.lastRecharge.amount} FCFA`;
    },

    // ENHANCED: Number formatting (ADDED FROM RAPPORTSSALARIE)
    formatNumber(value) {
      if (!value || value === 0) return "0";
      return new Intl.NumberFormat('fr-FR').format(value);
    },

    // ENHANCED: Improved recharge loading with comprehensive error handling
    async loadRecharges() {
      // ✅ Check for required data instead of transactions.length
      if (!this.balanceInfos.restaurantId || !this.balanceInfos.companyId) {
        console.log("Missing required data for loading recharges:", {
          restaurantId: this.balanceInfos.restaurantId,
          companyId: this.balanceInfos.companyId
        });
        this.showAlert("warning", "Données manquantes", "Informations du restaurant ou de l'entreprise non disponibles");
        return;
      }

      console.log("🚀 Loading recharges for admin view...");
      this.loadingRecharges = true;

      try {
        // CORRECTION : EXÉCUTION SÉQUENTIELLE
        await this.getRecharges();

        // Calculer les statistiques APRÈS avoir chargé toutes les recharges
        await this.getRechargeStatistics();

        console.log("✅ Recharges loaded successfully");

        this.$nextTick(() => {
          this.calculateRechargeStatsFromTransactions();
        });

        if (this.recharges.length === 0) {
          console.log("ℹ️ No recharge history found for this company/restaurant combination");
          this.showAlert("info", "Information",
              "Aucun historique de recharge trouvé pour cette combinaison restaurant/entreprise.");
        }

      } catch (error) {
        console.error("❌ Error during recharges loading:", error);
        this.showAlert("error", "Erreur", "Impossible de charger l'historique des recharges");
      } finally {
        this.loadingRecharges = false;
        console.log("🏁 Recharges loading finished");

        this.$nextTick(() => {
          this.debugRecharges();
        });
      }
    },

    // ENHANCED: Improved recharge fetching with better employee data handling
    async getRecharges() {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");

      try {
        // ✅ CORRECTED: Use restaurant-specific endpoint like RapportsRestaurant.vue
        const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/history/restaurant/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}`;

        console.log("=== RESTAURANT RECHARGES API CALL (ADMIN) ===");
        console.log("URL:", url);
        console.log("Params:", {
          startDate: startDate,
          endDate: endDate,
          requesterId: sessionStorage.getItem("accountId")
        });

        const response = await apiClient.get(url, {
          params: {
            startDate: startDate,
            endDate: endDate,
            requesterId: sessionStorage.getItem("accountId")
          }
        });

        console.log("📊 Raw restaurant recharges API response (ADMIN):", response.data);

        if (response.data && Array.isArray(response.data)) {
          this.recharges = response.data.map(item => {
            return {
              id: item.id,
              dateTransaction: item.dateTransaction,
              amount: item.amount,
              typeOperation: item.typeOperation,
              typeAuth: item.typeAuth,
              level: item.level,
              status: item.status,
              reason: item.reason || 'N/A',
              motifFailed: item.motifFailed,
              transactionUid: item.transactionUid,
              balanceBefore: item.balanceBefore,
              balanceAfter: item.balanceAfter,
              origin: item.origin,
              senderId: item.senderId,
              accountId: item.accountId,
              // CORRIGÉ : Utiliser directement les données employee du backend
              employeeId: item.employee?.userId || 'N/A',
              employeeFirstname: item.employee?.accountFirstname || 'N/A',
              employeeLastname: item.employee?.accountLastname || 'N/A',
              employeeEmail: item.employee?.accountEmail || 'N/A',
              paymentMethod: item.paymentMethod || 'N/A'
            };
          });

          console.log("🔍 Données brutes API:", response.data[0]);
          console.log("🔍 PaymentMethod mappé:", this.recharges[0].paymentMethod);
          console.log("✅ Mapped restaurant recharges (ADMIN):", this.recharges);

          // Extract unique payment methods for filter options
          const paymentMethods = [...new Set(this.recharges.map(r => r.paymentMethod).filter(pm => pm && pm !== 'N/A'))];
          this.paymentMethodRechargeOptions = ["Tous", ...paymentMethods];
          console.log("Payment method options for recharges:", this.paymentMethodRechargeOptions);
        } else if (response.data && response.data.status === 200 && response.data.datas) {
          // Alternative response structure
          console.log("📋 Recharges data in .datas property:", response.data.datas);

          this.recharges = response.data.datas.map(item => ({
            id: item.id,
            dateTransaction: item.dateTransaction,
            amount: item.amount,
            typeOperation: item.typeOperation,
            typeAuth: item.typeAuth,
            level: item.level,
            status: item.status,
            reason: item.reason || 'N/A',
            motifFailed: item.motifFailed,
            transactionUid: item.transactionUid,
            balanceBefore: item.balanceBefore,
            balanceAfter: item.balanceAfter,
            origin: item.origin,
            senderId: item.senderId,
            accountId: item.accountId,
            paymentMethod: item.paymentMethod || 'N/A',

            // CORRIGÉ : Utiliser directement les données employee du backend
            employeeId: item.employee?.userId || 'N/A',
            employeeFirstname: item.employee?.accountFirstname || 'N/A',
            employeeLastname: item.employee?.accountLastname || 'N/A',
            employeeEmail: item.employee?.accountEmail || 'N/A',
          }));

          console.log("✅ Mapped restaurant recharges from .datas (ADMIN):", this.recharges);

          // Extract unique payment methods for filter options
          const paymentMethods = [...new Set(this.recharges.map(r => r.paymentMethod).filter(pm => pm && pm !== 'N/A'))];
          this.paymentMethodRechargeOptions = ["Tous", ...paymentMethods];
          console.log("Payment method options for recharges:", this.paymentMethodRechargeOptions);
        } else {
          console.log("⚠️ Unexpected response structure or empty data:", response.data);
          this.recharges = [];
        }
      } catch (error) {
        console.error("❌ Error loading restaurant recharges (ADMIN):", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        this.recharges = [];
      }
    },

// ✅ NEW METHOD: Fallback approach using employee list
    async getRechargesFallback() {
      try {
        // If we have transactions, use employee IDs from there
        if (this.transactions && this.transactions.length > 0) {
          const employeeIds = [...new Set(this.transactions.map(t => t.matricule))];
          await this.getRechargesForEmployees(employeeIds);
        } else {
          // If no transactions yet, load for all company employees
          await this.getRechargesForAllCompanyEmployees();
        }
      } catch (error) {
        console.error("❌ Error in recharges fallback:", error);
        this.recharges = [];
      }
    },

// ✅ NEW METHOD: Get recharges for all company employees
      async getRechargesForAllCompanyEmployees() {
        try {
          // Get all employees for this company
          const employeesUrl = `${this.$store.getters.user.baseUrl}/account/users/company/${this.balanceInfos.companyId}`;
          const employeesResponse = await apiClient.get(employeesUrl);

          if (employeesResponse.data && employeesResponse.data.status === 200 && employeesResponse.data.datas) {
            const employeeIds = employeesResponse.data.datas.map(emp => emp.userId);
            console.log("👥 Found", employeeIds.length, "company employees");
            await this.getRechargesForEmployees(employeeIds);
          } else {
            console.log("⚠️ No employees found for company");
            this.recharges = [];
          }
        } catch (error) {
          console.error("❌ Error getting company employees:", error);
          this.recharges = [];
        }
      },

// ✅ NEW METHOD: Get recharges for specific employees
    async getRechargesForEmployees(employeeIds) {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");
      const allRecharges = [];

      for (const matricule of employeeIds) {
        try {
          const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/history/employee/${matricule}/${this.balanceInfos.companyId}`;

          const response = await apiClient.get(url, {
            params: {
              startDate: startDate,
              endDate: endDate,
              requesterId: sessionStorage.getItem("accountId")
            }
          });

          if (response.data && Array.isArray(response.data)) {
            const employeeRecharges = response.data.map(item => ({
              id: item.id,
              dateTransaction: item.dateTransaction,
              amount: item.amount,
              typeOperation: item.typeOperation,
              typeAuth: item.typeAuth,
              level: item.level,
              status: item.status,
              reason: item.reason || 'N/A',
              motifFailed: item.motifFailed,
              transactionUid: item.transactionUid,
              balanceBefore: item.balanceBefore,
              balanceAfter: item.balanceAfter,
              origin: item.origin,
              senderId: item.senderId,
              paymentMethod: item.paymentMethod || 'N/A',
              // Add employee info
              employeeId: item.employee?.userId || matricule,
              employeeFirstname: item.employee?.accountFirstname || this.getEmployeeFirstname(matricule),
              employeeLastname: item.employee?.accountLastname || this.getEmployeeLastname(matricule)
            }));

            allRecharges.push(...employeeRecharges);

            const paymentMethods = [...new Set(this.recharges.map(r => r.paymentMethod).filter(pm => pm && pm !== 'N/A'))];
            this.paymentMethodRechargeOptions = ["Tous", ...paymentMethods];
            console.log("Payment method options for recharges:", this.paymentMethodRechargeOptions);

            console.log("📋 Total recharges loaded:", this.recharges.length);
          }
        } catch (employeeError) {
          console.warn(`⚠️ Error for employee ${matricule}:`, employeeError);
          // Continue with other employees
        }
      }

      this.recharges = allRecharges;
      console.log("📋 Total recharges loaded:", this.recharges.length);
    },

    getEmployeeFirstname(matricule) {
      console.log("🔍 Searching firstname for matricule:", matricule);

      // ✅ If no transactions loaded yet, try to get from cached employee data
      if (!this.transactions || this.transactions.length === 0) {
        console.log("⚠️ No transactions available, using fallback");
        return this.getEmployeeInfoFromCache(matricule, 'firstname') || 'N/A';
      }

      const transaction = this.transactions.find(t => t.matricule === matricule);

      if (transaction) {
        console.log("✅ Firstname found:", transaction.prenom);
        return transaction.prenom;
      } else {
        console.log("❌ No transaction found for matricule:", matricule);
        return this.getEmployeeInfoFromCache(matricule, 'firstname') || 'N/A';
      }
    },

    getEmployeeLastname(matricule) {
      console.log("🔍 Searching lastname for matricule:", matricule);

      // ✅ If no transactions loaded yet, try to get from cached employee data
      if (!this.transactions || this.transactions.length === 0) {
        console.log("⚠️ No transactions available, using fallback");
        return this.getEmployeeInfoFromCache(matricule, 'lastname') || 'N/A';
      }

      const transaction = this.transactions.find(t => t.matricule === matricule);

      if (transaction) {
        console.log("✅ Lastname found:", transaction.nom);
        return transaction.nom;
      } else {
        console.log("❌ No transaction found for matricule:", matricule);
        return this.getEmployeeInfoFromCache(matricule, 'lastname') || 'N/A';
      }
    },

    getEmployeeInfoFromCache(matricule, type) {
      try {
        // Check if we have cached employee data
        const cachedEmployees = JSON.parse(sessionStorage.getItem('employeeCache') || '{}');

        if (cachedEmployees[matricule]) {
          const employee = cachedEmployees[matricule];
          return type === 'firstname' ? employee.firstname : employee.lastname;
        }

        // If not in cache, we'll return null and let the calling method handle it
        return null;
      } catch (error) {
        console.error("❌ Error getting employee info from cache:", error);
        return null;
      }
    },

// ✅ NEW METHOD: Cache employee data for later use
    cacheEmployeeData() {
      try {
        if (this.transactions && this.transactions.length > 0) {
          const employeeCache = {};

          this.transactions.forEach(t => {
            if (t.matricule && t.nom && t.prenom) {
              employeeCache[t.matricule] = {
                firstname: t.prenom,
                lastname: t.nom,
                matricule: t.matricule
              };
            }
          });

          sessionStorage.setItem('employeeCache', JSON.stringify(employeeCache));
          console.log("💾 Employee data cached for", Object.keys(employeeCache).length, "employees");
        }
      } catch (error) {
        console.error("❌ Error caching employee data:", error);
      }
    },

    // ENHANCED: Improved statistics calculation with error handling
    // Enhanced getRechargeStatistics method adapted for restaurant admin context
    async getRechargeStatistics() {
      // 🆕 NOUVEAU : Utiliser les mêmes dates que le filtrage dynamique
      const startDate = this.dateDebutRecharges;
      const endDate = this.dateFinRecharges;

      // ✅ CORRECTED: Use restaurant-specific endpoint like RapportsRestaurant.vue
      const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/statistics/restaurant/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}`;

      try {
        console.log("=== RESTAURANT RECHARGE STATISTICS API CALL (ADMIN - SYNCHRONIZED) ===");
        console.log("URL:", url);
        console.log("🆕 SYNCHRONIZED Params:", {
          startDate: startDate,
          endDate: endDate,
          requesterId: sessionStorage.getItem("accountId")
        });

        const response = await apiClient.get(url, {
          params: {
            startDate: startDate,    // 🆕 SYNCHRONISÉ avec filtrage
            endDate: endDate,        // 🆕 SYNCHRONISÉ avec filtrage
            requesterId: sessionStorage.getItem("accountId")
          }
        });

        console.log("📊 Restaurant recharge statistics response (ADMIN - SYNCHRONIZED):", response.data);

        if (response.data) {
          this.rechargeStats = response.data;
          console.log("✅ Restaurant recharge stats set (ADMIN - SYNCHRONIZED):", this.rechargeStats);
        }
      } catch (error) {
        console.error("❌ Error loading restaurant recharge statistics (ADMIN):", error);
        console.error("Stats error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        // 🆕 FALLBACK : Stats vides si erreur
        this.rechargeStats = {
          totalCredits: 0,
          countCredits: 0,
          lastRecharge: null
        };
      }
    },

// ✅ NEW METHOD: Fallback calculation from loaded data
    calculateRechargeStatsFromLoadedData() {
      try {
        if (this.recharges.length === 0) {
          this.rechargeStats = {
            totalCredits: 0,
            countCredits: 0,
            lastRecharge: null
          };
          return;
        }

        // Filter successful credits
        const successfulCredits = this.recharges.filter(r =>
            r.typeOperation === 'CREDIT' && r.status === 'SUCCESS'
        );

        // Calculate total credits
        const totalCredits = successfulCredits.reduce((sum, r) => sum + r.amount, 0);
        const countCredits = successfulCredits.length;

        // Find last recharge
        const lastRecharge = successfulCredits
            .sort((a, b) => new Date(b.dateTransaction) - new Date(a.dateTransaction))[0];

        this.rechargeStats = {
          totalCredits: totalCredits,
          countCredits: countCredits,
          lastRecharge: lastRecharge ? {
            date: lastRecharge.dateTransaction,
            amount: lastRecharge.amount
          } : null
        };

        console.log("📊 Fallback recharge statistics calculated:", this.rechargeStats);

      } catch (error) {
        console.error("❌ Error in fallback calculation:", error);
        this.rechargeStats = {
          totalCredits: 0,
          countCredits: 0,
          lastRecharge: null
        };
      }
    },

    updateDateRangeRecharges(period) {
      moment.locale('fr');

      console.log("🔄 Updating date range for period:", period);

      switch (period) {
        case 'today':
          this.dateDebutRecharges = moment().format('YYYY-MM-DD');
          this.dateFinRecharges = moment().format('YYYY-MM-DD');
          break;
        case 'week': {
          // 🆕 CORRIGÉ : Limiter la semaine aux bornes du mois actuel
          let weekStart = moment().startOf('isoWeek');
          let weekEnd = moment().endOf('isoWeek');
          const monthStart = moment().startOf('month');
          const monthEnd = moment().endOf('month');

          // Limiter la semaine au mois actuel pour éviter les débordements
          if (weekStart.isBefore(monthStart)) {
            console.log("⚠️ Semaine déborde avant le mois, limitation au début du mois");
            weekStart = monthStart;
          }
          if (weekEnd.isAfter(monthEnd)) {
            console.log("⚠️ Semaine déborde après le mois, limitation à la fin du mois");
            weekEnd = monthEnd;
          }

          this.dateDebutRecharges = weekStart.format('YYYY-MM-DD');
          this.dateFinRecharges = weekEnd.format('YYYY-MM-DD');

          console.log("📅 Semaine limitée au mois:", this.dateDebutRecharges, "au", this.dateFinRecharges);
          break;
        }
        case 'month':
          this.dateDebutRecharges = moment().startOf('month').format('YYYY-MM-DD');
          this.dateFinRecharges = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'last_month':
          this.dateDebutRecharges = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.dateFinRecharges = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
        default:
          // Période personnalisée - garder les dates existantes
          break;
      }

      console.log("📅 New date range:", this.dateDebutRecharges, "to", this.dateFinRecharges);

      // 🔧 NOUVEAU : Déclencher rechargement des stats SEULEMENT si on a des données
      if (this.activeTab === 'tab-recharges' && this.recharges.length > 0) {
        this.$nextTick(() => {
          this.calculateRechargeStatsFromTransactions();
        });
      }
    },

    // ENHANCED: Utility methods for recharges (MAINTAINED FROM ORIGINAL)
    getTypeOperationColor(type) {
      return type === "CREDIT" ? "success" : "error";
    },

    getTypeOperationIcon(type) {
      return type === "CREDIT" ? "mdi-plus" : "mdi-minus";
    },

    getStatusColorRecharge(status) {
      const colors = {
        "SUCCESS": "success",
        "FAILED": "error",
        "CANCELLED": "warning",
        "PENDING": "info"
      };
      return colors[status] || "grey";
    },

    getStatusIconRecharge(status) {
      const icons = {
        "SUCCESS": "mdi-check",
        "FAILED": "mdi-close",
        "CANCELLED": "mdi-cancel",
        "PENDING": "mdi-clock"
      };
      return icons[status] || "mdi-help";
    },

    getAmountClass(typeOperation) {
      return {
        'font-weight-bold': true,
        'success--text': typeOperation === 'CREDIT',
        'error--text': typeOperation === 'DEBIT'
      };
    },

    getAuthColor(typeAuth) {
      const colors = {
        "MANUEL": "primary",
        "CARTE": "info",
        "BIO": "purple",
        "AUTOMATIC": "orange"
      };
      return colors[typeAuth] || "grey";
    },

        getPaymentMethodRechargeColor(method) {
      if (!method || method === 'N/A') return 'grey';

      const colors = {
        "CASH": "green",
        "BANK_TRANSFER": "blue",
        "CHECK": "orange",
        "MOBILE_MONEY": "purple",
        "CARD": "indigo",
        "CREDIT_CARD": "indigo",
        "DEBIT_CARD": "teal",
        "ELECTRONIC": "cyan",
        "WAVE": "blue",
        "ORANGE_MONEY": "orange",
        "FREE_MONEY": "red",
        "ADMIN_CREDIT": "primary",
        "SYSTEM": "grey"
      };

      return colors[method.toUpperCase()] || "grey";
    },

    getPaymentMethodRechargeIcon(method) {
      if (!method || method === 'N/A') return 'mdi-help-circle';

      const icons = {
        "CASH": "mdi-cash",
        "BANK_TRANSFER": "mdi-bank-transfer",
        "CHECK": "mdi-checkbook",
        "MOBILE_MONEY": "mdi-cellphone",
        "CARD": "mdi-credit-card",
        "CREDIT_CARD": "mdi-credit-card",
        "DEBIT_CARD": "mdi-credit-card-outline",
        "ELECTRONIC": "mdi-web",
        "WAVE": "mdi-water",
        "ORANGE_MONEY": "mdi-phone",
        "FREE_MONEY": "mdi-gift",
        "ADMIN_CREDIT": "mdi-account-supervisor",
        "SYSTEM": "mdi-cog"
      };

      return icons[method.toUpperCase()] || "mdi-wallet";
    },

    // ENHANCED: Improved recharge details dialog
    viewDetailsRecharge(item) {
      const details = `
        <strong style="color: #4CAF50;">👤 Employé</strong><br>
        ${item.employeeLastname} ${item.employeeFirstname} (${item.employeeId})<br><br>

        <strong style="color: #4CAF50;">💰 Montant</strong><br>
        <span style="font-size: 18px; font-weight: bold; color: ${item.typeOperation === 'CREDIT' ? '#4CAF50' : '#F44336'};">
          ${item.typeOperation === 'DEBIT' ? '-' : '+'}${item.amount} FCFA
        </span><br><br>

        <strong style="color: #4CAF50;">📅 Date et Heure</strong><br>
        ${this.formatDate(item.dateTransaction)}<br><br>

        <strong style="color: #4CAF50;">🔄 Type d'Opération</strong><br>
        ${item.typeOperation}<br><br>

        <strong style="color: #4CAF50;">💳 Méthode de Paiement</strong><br>
        ${item.paymentMethod || 'N/A'}<br><br>

        <strong style="color: #4CAF50;">🔐 Authentification</strong><br>
        ${item.typeAuth}<br><br>

        <strong style="color: #4CAF50;">📊 Niveau</strong><br>
        ${this.formatNiveau(item.level)}<br><br>

        <strong style="color: #4CAF50;">✅ Statut</strong><br>
        ${item.status}<br><br>

        <strong style="color: #4CAF50;">📝 Raison</strong><br>
        ${item.reason}<br><br>

        <strong style="color: #4CAF50;">💳 Soldes</strong><br>
        Avant: ${item.balanceBefore} FCFA<br>
        Après: ${item.balanceAfter} FCFA<br><br>

        <hr style="margin: 15px 0;">
        <small style="color: #757575;">
          Transaction ID: ${item.transactionUid}<br>
          Référence: #${item.id}
        </small>
      `;

      this.detailsRechargeDialog = true;
      this.detailsRechargeContent = details;
    },

    clearSearchRecharges() {
      this.searchQueryRecharges = "";
    },

    canCancelRecharge(item) {
      // Only admin can cancel
      if (sessionStorage.getItem("userId")?.toLowerCase() !== "admin") {
        return false;
      }

      // Only allow cancellation if status is SUCCESS (exact match)
      if (item.status !== "SUCCESS") {
        return false;
      }

      // Only allow cancellation within 24 hours
      const hoursDiff = moment().diff(moment(item.dateTransaction), 'hours');
      if (hoursDiff > 24) {
        return false;
      }

      return true;
    },

// 3. EXTERNAL PAYMENTS TABLE - Add cancellation capability if needed
    canCancelExternalPayment(item) {
      // Only admin can cancel
      if (sessionStorage.getItem("userId")?.toLowerCase() !== "admin") {
        return false;
      }

      // Only allow cancellation if status is SUCCESS
      if (item.status !== "SUCCESS") {
        return false;
      }

      // Only allow cancellation within 2 hours for external payments
      const hoursDiff = moment().diff(moment(item.transactionDate), 'hours');
      if (hoursDiff > 2) {
        return false;
      }

      return true;
    },

    async cancelRecharge(item) {
      // Double-check permissions before cancellation
      if (!this.canCancelRecharge(item)) {
        this.showAlert("error", "Action non autorisée", "Impossible d'annuler cette recharge.");
        return;
      }

      // Additional status verification
      if (item.status !== "SUCCESS") {
        this.showAlert("error", "Statut invalide", "Seules les recharges avec le statut 'SUCCESS' peuvent être annulées.");
        return;
      }

      const reason = prompt("Raison de l'annulation:");
      if (!reason) return;

      this.loadingRecharges = true;
      try {
        const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/cancel/${item.transactionUid}`;
        const response = await apiClient.post(url, null, {
          params: {reason: reason}
        });

        if (response.data.status === 200) {
          this.showAlert("success", "Succès", "Recharge annulée avec succès");
          await this.loadRecharges();
        } else {
          this.showAlert("error", "Erreur", response.data.message);
        }
      } catch (error) {
        this.showAlert("error", "Erreur", "Impossible d'annuler la recharge");
      } finally {
        this.loadingRecharges = false;
      }
    },

    isValidStatusForCancellation(status, type = 'transaction') {
      if (!status) return false;

      // Normalize status comparison
      const normalizedStatus = status.toString().toUpperCase();
      return normalizedStatus === 'SUCCESS';
    },

    formatDate(date) {
      if (!date || date === "0") return "N/A";
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

// AJOUT : Méthodes de gestion des stats (EXACTEMENT comme RapportsRestaurant.vue)
    getStatValue(statKey) {
      if (!this.rechargeStats) return "0";

      const value = this.rechargeStats[statKey];
      if (value === null || value === undefined) return "0";

      if (statKey.includes('total') || statKey.includes('Credits') || statKey.includes('Debits')) {
        return this.formatNumber(value);
      }

      return value.toString();
    },

// AJOUT : Méthodes de gestion des messages no-data (EXACTEMENT comme RapportsRestaurant.vue)
    getNoDataText() {
      if (this.loadingRecharges) {
        return "Chargement des recharges...";
      }
      if (this.recharges.length === 0) {
        return "Aucune recharge trouvée";
      }
      return "Aucune recharge correspond aux filtres sélectionnés";
    },

    getNoDataTitle() {
      if (this.recharges.length === 0) {
        return "Aucun historique de recharge";
      }
      return "Aucun résultat";
    },

    debugRecharges() {
      console.log("=== RESTAURANT RECHARGES DEBUG (ADMIN) ===");
      console.log("Raw recharges:", this.recharges);
      console.log("Recharge stats:", this.rechargeStats);
      console.log("Loading state:", this.loadingRecharges);
      console.log("Restaurant info:", {
        nom: this.balanceInfos.onlyRestaurantNameSelect,
        id: this.balanceInfos.restaurantId,
        companyId: this.balanceInfos.companyId
      });
      console.log("Date filters:", {
        dateDebutRecharges: this.dateDebutRecharges,
        dateFinRecharges: this.dateFinRecharges,
        selectedPeriodRecharges: this.selectedPeriodRecharges
      });
      console.log("Other filters:", {
        selectedTypeRecharge: this.selectedTypeRecharge,
        selectedStatusRecharge: this.selectedStatusRecharge,
        searchQueryRecharges: this.searchQueryRecharges
      });
      console.log("Filtered recharges:", this.filteredRecharges);
      console.log("Headers recharges:", this.headersRecharges);
      console.log("=====================================");
    },

    // ENHANCED: Improved refresh functionality with recharge support
    async refreshData() {
      try {
        console.log("🔄 Refreshing all data...");

        // Reset search and filters
        this.searchName = "";
        this.depthVal = {name: "Profondeur Aujourd'hui", value: "0"};
        this.heightVal = {name: "Hauteur Aujourd'hui", value: "0"};

        // Reset recharge filters
        this.selectedPeriodRecharges = "month";
        this.selectedTypeRecharge = "Tous";
        this.selectedPaymentMethodRecharge = "Tous";
        this.selectedStatusRecharge = "Tous";
        this.searchQueryRecharges = "";
        this.updateDateRangeRecharges("month");

        // Reset external payment filters
        this.selectedPeriodExternal = "month";
        this.selectedPaymentMethod = "Tous";
        this.selectedStatusExternal = "Tous";
        this.searchQueryExternal = "";
        this.updateDateRangeExternal("month");

        // Reload restaurant and company lists
        await Promise.all([
          this.loadRestaurants(),
          this.loadCompanies()
        ]);

        if (!this.restaurantFound) {
          this.showAlert("warning", "Aucun restaurant sélectionné", "Veuillez d'abord rechercher un restaurant");
          return;
        }

        // ✅ Load data for current active tab immediately
        await this.loadDataForCurrentTab();

        // ✅ Load basic restaurant data in background (non-blocking)
        this.loadData().catch(error => {
          console.error("Error loading background data:", error);
        });

        let message = "Les données ont été mises à jour";

// Dans la logique de rechargement par onglet :
        if (this.activeTab === 'tab-recharges') {
          await this.loadRecharges();
          // 🆕 NOUVEAU: Recharger les stats avec la période courante
          await this.getRechargeStatistics();
          // 🆕 NOUVEAU: Recalculer les stats après chargement
          this.$nextTick(() => {
            this.calculateRechargeStatsFromTransactions();
          });
          message = "Données actualisées. " + (this.recharges.length === 0 ?
              "Aucun historique de recharge disponible pour cette combinaison restaurant/entreprise." :
              "Historique des recharges mis à jour.");
        } else if (this.activeTab === 'tab-paiements-externes') {
          await this.loadExternalPayments();
          // NOUVEAU : Recalculer les stats après le chargement
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }

        this.showAlert("success", "Actualisation réussie", message);
        this.$vuetify.goTo(0);

      } catch (error) {
        console.error("❌ Error during refresh:", error);
        this.showAlert("error", "Erreur", "Erreur lors de l'actualisation des données");
      }
    },

// ✅ NEW METHOD: Load data for the current active tab
    async loadDataForCurrentTab() {
      switch (this.activeTab) {
        case 'tab-recharges':
          await this.loadRecharges();
          break;
        case 'tab-paiements-externes':
          await this.loadExternalPayments();
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
          break;
        case 'tab-transactions':
          // Will be loaded by background loadData()
          break;
      }
    },

    async searchRestaurant() {
      if (!this.canSearch) {
        this.showAlert("warning", "Données invalides", "Veuillez sélectionner un restaurant et une entreprise.");
        return;
      }

      this.loading = true;
      this.restaurantFound = false;

      try {
        // Find IDs
        let itemPreviousCompany = "";
        this.balanceInfos.onlyCompaniesListFull.forEach(item => {
          if (item === this.balanceInfos.onlyCompanyNameSelect) {
            this.balanceInfos.companyId = itemPreviousCompany;
          }
          itemPreviousCompany = item;
        });

        let itemPreviousRestaurant = "";
        this.balanceInfos.onlyRestaurantsListFull.forEach(item => {
          if (item === this.balanceInfos.onlyRestaurantNameSelect) {
            this.balanceInfos.restaurantId = itemPreviousRestaurant;
          }
          itemPreviousRestaurant = item;
        });

        // ✅ SET RESTAURANT FOUND IMMEDIATELY - This allows other tabs to load independently
        this.restaurantFound = true;

        // ✅ Stop loading immediately so UI shows the tabs
        this.loading = false;

        this.showAlert("success", "Restaurant trouvé", "Les données du restaurant ont été chargées avec succès");

        // ✅ Load data for the current active tab only, don't wait for it
        this.loadDataForActiveTab();

        // ✅ Load basic data in background (non-blocking)
        this.loadData().catch(error => {
          console.error("Error loading basic data:", error);
        });

      } catch (error) {
        this.showAlert("error", "Erreur", "Erreur lors de la recherche du restaurant");
        console.error("Erreur lors de la recherche:", error);
        this.loading = false;
      }
    },

// ✅ NEW METHOD: Load data specific to the currently active tab
    loadDataForActiveTab() {
      switch (this.activeTab) {
        case 'tab-transactions':
          // Transactions will be loaded by loadData() in background
          break;
        case 'tab-recharges':
          this.loadRecharges();
          break;
        case 'tab-paiements-externes':
          this.loadExternalPayments();
          break;
        default:
          // Default to loading basic transaction data
          break;
      }
    },

    async loadData() {
      if (!this.restaurantFound) return;

      // Don't set loading to true here since this runs in background
      console.log("🔄 Loading basic restaurant data in background...");

      try {
        await this.getDetailsFinancialsDatas();
        console.log("✅ Basic restaurant data loaded successfully");
      } catch (error) {
        console.error("❌ Error lors du chargement des données:", error);
        this.showAlert("error", "Erreur de chargement", "Impossible de charger les données");
      }
    },

    async loadBasicDataInBackground() {
      try {
        console.log("Loading basic restaurant data in background...");

        // Load basic restaurant and financial details
        await this.getDetailsFinancialsDatas();

        console.log("Basic restaurant data loaded successfully");
      } catch (error) {
        console.error("Error loading basic data in background:", error);
      }
    },

    // ✅ UPDATED: Add employee caching to getDetailsFinancialsDatas
    async getDetailsFinancialsDatas() {
      const endDate = moment().add(this.heightVal.value, "days").format("YYYY-MM-DD");
      const startDate = moment().subtract(this.depthVal.value, "days").format("YYYY-MM-DD");

      const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_restaurant_transactions_by_company_all/${startDate}/${endDate}/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas || [];

          this.transactions = data.map(item => ({
            id: item.id,
            entreprise: this.balanceInfos.onlyCompanyNameSelect,
            nom: item.employee.accountLastname,
            prenom: item.employee.accountFirstname,
            matricule: item.employee.userId,
            dateCommande: item.dateCommand,
            montant: item.price,
            typeOperation: item.typeTrs,
            paiement: item.typeAuth,
            statut: item.status,
            infos: item.infos,
            origine: item.orderOrigin,
            niveau: item.level,
            motifRefus: item.motifFailed,
            dateTransaction: item.dateTransaction,
            transactionUid: item.transaction_uid
          }));

          // ✅ NEW: Cache employee data for independent loading
          this.cacheEmployeeData();

          await this.getFinancialInfosByCompany();
        } else {
          this.showAlert("warning", "Avertissement", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des transactions:", error);
      }
    },

    /**
     * NOUVEAU : Calcul des statistiques enrichies
     */
    calculateEnhancedStats() {
      // Pourcentages de répartition pour le mois actuel
      if (this.personalTransactionsStats.currentMonthTotal > 0) {
        this.personalTransactionsStats.walletPercentage =
            (this.personalTransactionsStats.currentMonthWallet / this.personalTransactionsStats.currentMonthTotal * 100);
        this.personalTransactionsStats.externalPercentage =
            (this.personalTransactionsStats.currentMonthExternal / this.personalTransactionsStats.currentMonthTotal * 100);
      } else {
        this.personalTransactionsStats.walletPercentage = 25; // Valeur par défaut
        this.personalTransactionsStats.externalPercentage = 75; // Valeur par défaut
      }

      // Taux de croissance spécifique aux transactions externes
      if (this.personalTransactionsStats.previousMonthExternal > 0) {
        const externalDiff = this.personalTransactionsStats.currentMonthExternal - this.personalTransactionsStats.previousMonthExternal;
        this.personalTransactionsStats.externalGrowthRate =
            (externalDiff / this.personalTransactionsStats.previousMonthExternal * 100);
      } else {
        this.personalTransactionsStats.externalGrowthRate = 0;
      }

      console.log("📈 Enhanced stats calculated:", {
        walletPercentage: this.personalTransactionsStats.walletPercentage,
        externalPercentage: this.personalTransactionsStats.externalPercentage,
        externalGrowthRate: this.personalTransactionsStats.externalGrowthRate
      });
    },

    // ENHANCED: Calculate personal transaction amounts
    calculatePersonalTransactionsAmounts() {
      // Montants pour le mois actuel
      const actualAmountWallet = this.personalTransactionsStats.currentMonthWallet * this.unitPriceTransaction;
      const actualAmountExternal = this.personalTransactionsStats.currentMonthExternal * this.unitPriceTransaction;
      const actualAmountTotal = this.personalTransactionsStats.currentMonthTotal * this.unitPriceTransaction;

      // Montants pour le mois précédent
      const previousAmountWallet = this.personalTransactionsStats.previousMonthWallet * this.unitPriceTransaction;
      const previousAmountExternal = this.personalTransactionsStats.previousMonthExternal * this.unitPriceTransaction;
      const previousAmountTotal = this.personalTransactionsStats.previousMonthTotal * this.unitPriceTransaction;

      // Mise à jour des montants d'affichage
      this.sensNumerikInfos.actualMonthEncours = this.formatNumberWithCommas(actualAmountTotal) + " F CFA";
      this.sensNumerikInfos.previousMonthEncours = this.formatNumberWithCommas(previousAmountTotal) + " F CFA";

      // NOUVEAU : Détails de répartition pour l'affichage enrichi
      this.personalTransactionsStats.breakdown = {
        wallet: {
          description: "Transactions via porte-monnaie personnel (solde épuisé)",
          currentAmount: actualAmountWallet,
          previousAmount: previousAmountWallet
        },
        external: {
          description: "Transactions externes 100% cash (paiement comptoir)",
          currentAmount: actualAmountExternal,
          previousAmount: previousAmountExternal
        }
      };

      console.log("💰 Enhanced amounts calculated:", {
        wallet: {current: actualAmountWallet, previous: previousAmountWallet},
        external: {current: actualAmountExternal, previous: previousAmountExternal},
        total: {current: actualAmountTotal, previous: previousAmountTotal}
      });
    },

    // ENHANCED: Methods for personal transaction evolution display
    formatDifference() {
      const diff = this.personalTransactionsStats.difference;
      if (diff === 0) return "0";
      return diff > 0 ? `+${this.formatNumberWithCommas(diff)}` : this.formatNumberWithCommas(diff);
    },

    formatPercentage() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage === 0) return "0%";
      return percentage > 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;
    },

    getDifferenceColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success lighten-4";
      if (diff < 0) return "error lighten-4";
      return "grey lighten-3";
    },

    getDifferenceIcon() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "mdi-trending-up";
      if (diff < 0) return "mdi-trending-down";
      return "mdi-minus";
    },

    getDifferenceIconColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success";
      if (diff < 0) return "error";
      return "grey";
    },

    getDifferenceTextColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success--text";
      if (diff < 0) return "error--text";
      return "grey--text";
    },

    getPercentageCardColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success lighten-4";
      if (percentage < 0) return "error lighten-4";
      return "grey lighten-3";
    },

    getPercentageIcon() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "mdi-trending-up";
      if (percentage < 0) return "mdi-trending-down";
      return "mdi-equal";
    },

    getPercentageIconColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success";
      if (percentage < 0) return "error";
      return "grey";
    },

    getPercentageTextColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success--text";
      if (percentage < 0) return "error--text";
      return "grey--text";
    },

    getTrendCardColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary lighten-4";
      if (diff < 0) return "warning lighten-4";
      return "grey lighten-3";
    },

    getTrendIcon() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "mdi-arrow-up-bold";
      if (diff < 0) return "mdi-arrow-down-bold";
      return "mdi-arrow-right-bold";
    },

    getTrendIconColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary";
      if (diff < 0) return "warning";
      return "grey";
    },

    getTrendTextColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary--text";
      if (diff < 0) return "warning--text";
      return "grey--text";
    },

    getTrendText() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "HAUSSE";
      if (diff < 0) return "BAISSE";
      return "STABLE";
    },

    // ENHANCED: Number formatting with comma separator (ADDED FROM RAPPORTSSALARIE)
    formatNumberWithCommas(number) {
      return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    async getFinancialInfosByCompany() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_financial_infos_company/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas;

          // Update financial data
          Object.assign(this.financialDetails, data);

          // Update balances
          this.balanceInfos.globalRevenueActualMonth = data.globalRevenueActualMonth;
          this.balanceInfos.amountDueByCompanyActualMonth = data.amountDueByCompanyActualMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeeActualMonth = data.amountAlreadyPaidByEmployeeActualMonth;
          this.balanceInfos.globalRevenuePreviousMonth = data.globalRevenuePreviousMonth;
          this.balanceInfos.amountDueByCompanyPreviousMonth = data.amountDueByCompanyPreviousMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeePreviousMonth = data.amountAlreadyPaidByEmployeePreviousMonth;

          // Load unit price after financial info
          this.getUnitPriceTransaction();
        }
      } catch (error) {
        console.error("Erreur lors du chargement des infos financières:", error);
      }
    },

    depthDate() {
      if (this.depthVal) {
        sessionStorage.setItem("depthValChoosedd", this.depthVal.value);
        sessionStorage.setItem("depthValChoosedt", this.depthVal.name);
      }
      this.getDetailsFinancialsDatas();
    },

    heightDate() {
      if (this.heightVal) {
        sessionStorage.setItem("heightValChoosedd", this.heightVal.value);
        sessionStorage.setItem("heightValChoosedt", this.heightVal.name);
      }
      this.getDetailsFinancialsDatas();
    },

    cleared() {
      this.searchName = "";
    },

    search() {
      // Search is now automatic via computed filteredTransactions
    },

    async exportToExcel() {
      this.exportLoading = true;

      try {
        const dataToExport = [];

        // Headers
        dataToExport.push([
          "Entreprise",
          "Nom",
          "Prenom",
          "Matricule",
          "Date Commande",
          "Montant (FCFA)",
          "Type Operation",
          "Moyen de Paiement",
          "Statut",
          "Niveau",
          "Origine",
          "Informations",
          "Date Transaction"
        ]);

        // Data
        this.filteredTransactions.forEach(item => {
          dataToExport.push([
            item.entreprise,
            item.nom,
            item.prenom,
            item.matricule,
            this.convertDate(item.dateCommande),
            item.montant,
            item.typeOperation,
            item.paiement,
            item.statut,
            this.formatNiveau(item.niveau),
            item.origine || '',
            item.infos || '',
            this.convertDate(item.dateTransaction)
          ]);
        });

        // Create Excel file
        const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        // Adjust column widths
        const columnWidths = [
          {wch: 15}, {wch: 15}, {wch: 15}, {wch: 12},
          {wch: 15}, {wch: 12}, {wch: 15}, {wch: 15},
          {wch: 10}, {wch: 12}, {wch: 12}, {wch: 20}, {wch: 18}
        ];
        worksheet['!cols'] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, "Rapports Restaurant");

        // File name
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `Rapports_Restaurant_${this.balanceInfos.onlyRestaurantNameSelect}_${this.balanceInfos.onlyCompanyNameSelect}_${currentDate}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        this.showAlert("success", "Export réussi", `Le fichier ${fileName} a été téléchargé avec succès`);

      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        this.showAlert("error", "Erreur d'export", "Impossible d'exporter les données vers Excel");
      } finally {
        this.exportLoading = false;
      }
    },

    viewTransactionDetails(transaction) {
      this.selectedTransaction = transaction;
      this.detailsDialog = true;
    },

    canDelete(transaction) {
      // Only admin can delete
      if (sessionStorage.getItem("userId")?.toLowerCase() !== "admin") {
        return false;
      }

      // Only allow deletion if status is SUCCESS (case-insensitive)
      const status = transaction.statut?.toLowerCase();
      if (status !== "success") {
        return false;
      }

      // Don't allow deletion of internet transactions
      const origin = transaction.origine?.toLowerCase();
      if (origin === "internet") {
        return false;
      }

      return true;
    },

    async deleteTransaction(transaction) {
      // Double-check permissions before deletion
      if (!this.canDelete(transaction)) {
        this.showAlert("error", "Action non autorisée", "Vous n'avez pas les droits pour supprimer cette transaction ou elle ne peut pas être supprimée.");
        return;
      }

      // Additional status verification
      if (transaction.statut?.toLowerCase() !== "success") {
        this.showAlert("error", "Statut invalide", "Seules les transactions avec le statut 'SUCCESS' peuvent être supprimées.");
        return;
      }

      if (confirm("Êtes-vous sûr de vouloir supprimer cette transaction ?")) {
        this.loading = true;
        try {
          const url = `${this.$store.getters.user.baseUrl}/wallet/delete_wallet_transaction_by_uuid/${transaction.transactionUid}/${sessionStorage.getItem("accountId")}`;
          const response = await apiClient.delete(url);

          if (response.data.status === 200) {
            this.showAlert("success", "Succès", response.data.message);
            await this.getDetailsFinancialsDatas();
          } else {
            this.showAlert("error", "Erreur", response.data.message);
          }
        } catch (error) {
          this.showAlert("error", "Erreur", "Impossible de supprimer la transaction");
        } finally {
          this.loading = false;
        }
      }
    },

    // Utility methods
    isEmpty(str) {
      return (str === null || !str || 0 === str.length || /^\s*$/.test(str));
    },

    getInitials(nom, prenom) {
      const n = nom ? nom.charAt(0).toUpperCase() : "";
      const p = prenom ? prenom.charAt(0).toUpperCase() : "";
      return n + p;
    },

    getEmployeeColor(matricule) {
      const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F', '#00796B', '#5D4037', '#455A64'];
      const index = matricule ? matricule.length % colors.length : 0;
      return colors[index];
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "error";
        case "cancelled":
          return "warning";
        default:
          return "grey";
      }
    },

    getStatusColorHex(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "#4CAF50";
        case "failed":
          return "#F44336";
        case "cancelled":
          return "#FF9800";
        default:
          return "#9E9E9E";
      }
    },

    getNiveauColor(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'primary';
        case 'SECONDAIRE_COMPANY':
          return 'orange';
        case 'SECONDAIRE_PERSONNEL':
          return 'purple';
        default:
          return 'grey';
      }
    },

    formatNiveau(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'PRINCIPAL';
        case 'SECONDAIRE_COMPANY':
          return 'ENTREPRISE';
        case 'SECONDAIRE_PERSONNEL':
          return 'PERSONNEL';
        default:
          return niveau || 'N/A';
      }
    },

    getCurrentMonthName() {
      return this.financialDetails.monthActual || "Mois Actuel";
    },

    getPreviousMonthName() {
      return this.financialDetails.monthPrevious || "Mois Precedent";
    },

    convertDate(val) {
      if (!val) return "";
      return moment(val).format("DD-MM-YYYY");
    },

    datefull(val) {
      if (!val) return "";
      return moment(val).format("DD-MM-YYYY HH:mm:ss");
    },

    // ENHANCED: Centralized alert system (MAINTAINED FROM RAPPORTSSALARIE)
    showAlert(color, title, message) {
      this.alertColor = color;
      this.alertTitle = title;
      this.alertMessage = message;
      this.alertDialog = true;
    },

    /**
     * Obtient le total des transactions du mois actuel
     */
    getCurrentMonthTotal() {
      return this.personalTransactionsStats.currentMonthTotal ||
          this.personalTransactionsStats.currentMonth || 0;
    },

    /**
     * Obtient le total des transactions du mois précédent
     */
    getPreviousMonthTotal() {
      return this.personalTransactionsStats.previousMonthTotal ||
          this.personalTransactionsStats.previousMonth || 0;
    },

    /**
     * Calcule le pourcentage wallet pour le mois actuel
     */
    getCurrentWalletPercentage() {
      const total = this.getCurrentMonthTotal();
      if (total === 0) return 25; // Valeur par défaut pour l'affichage

      const wallet = this.personalTransactionsStats.currentMonthWallet ||
          Math.floor(total * 0.25); // Estimation par défaut
      return (wallet / total) * 100;
    },

    /**
     * Calcule le pourcentage external pour le mois actuel
     */
    getCurrentExternalPercentage() {
      return 100 - this.getCurrentWalletPercentage();
    },

    /**
     * Calcule le pourcentage wallet pour le mois précédent
     */
    getPreviousWalletPercentage() {
      const total = this.getPreviousMonthTotal();
      if (total === 0) return 25; // Valeur par défaut pour l'affichage

      const wallet = this.personalTransactionsStats.previousMonthWallet ||
          Math.floor(total * 0.25); // Estimation par défaut
      return (wallet / total) * 100;
    },

    /**
     * Calcule le pourcentage external pour le mois précédent
     */
    getPreviousExternalPercentage() {
      return 100 - this.getPreviousWalletPercentage();
    },

    /**
     * Retourne les informations de répartition formatées
     */
    getBreakdownInfo(type, period) {
      const isWallet = type === 'wallet';
      const isCurrent = period === 'current';

      let amount = 0;

      try {
        // Vérifier si les données détaillées sont disponibles
        if (this.personalTransactionsStats.breakdown &&
            this.personalTransactionsStats.breakdown[type]) {

          // Utiliser les données précalculées si disponibles
          const breakdownData = this.personalTransactionsStats.breakdown[type];
          amount = isCurrent ? breakdownData.currentAmount : breakdownData.previousAmount;

        } else {
          // Fallback : calculer à partir des données de base
          if (isCurrent) {
            if (isWallet) {
              const walletCount = this.personalTransactionsStats.currentMonthWallet ||
                  Math.floor(this.getCurrentMonthTotal() * 0.25);
              amount = walletCount * (this.unitPriceTransaction || 0);
            } else {
              const externalCount = this.personalTransactionsStats.currentMonthExternal ||
                  Math.ceil(this.getCurrentMonthTotal() * 0.75);
              amount = externalCount * (this.unitPriceTransaction || 0);
            }
          } else {
            if (isWallet) {
              const walletCount = this.personalTransactionsStats.previousMonthWallet ||
                  Math.floor(this.getPreviousMonthTotal() * 0.25);
              amount = walletCount * (this.unitPriceTransaction || 0);
            } else {
              const externalCount = this.personalTransactionsStats.previousMonthExternal ||
                  Math.ceil(this.getPreviousMonthTotal() * 0.75);
              amount = externalCount * (this.unitPriceTransaction || 0);
            }
          }
        }

        return this.formatNumberWithCommas(amount) + " F CFA";

      } catch (error) {
        console.error("❌ Error in getBreakdownInfo:", error);
        return "0 F CFA";
      }
    },

    /**
     * Enhanced method for getting personal transactions stats with wallet/external breakdown
     */
    async getPersonalTransactionsStatsEnhanced() {
      this.personalTransactionsStats.loading = true;
      this.personalTransactionsStats.error = null;

      // Utiliser l'endpoint enrichi qui inclut wallet + external breakdown
      const url = `${this.$store.getters.user.baseUrl}/wallet/personal-transactions/stats/enhanced/${this.balanceInfos.restaurantId}/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);

        if (response.status === 200) {
          const data = response.data.datas;

          console.log("📊 Enhanced personal transaction stats received:", data);

          // Mapper les nouvelles propriétés du DTO enrichi
          this.personalTransactionsStats.currentMonth = data.currentMonth || 0;
          this.personalTransactionsStats.previousMonth = data.previousMonth || 0;
          this.personalTransactionsStats.difference = data.difference || 0;
          this.personalTransactionsStats.percentageChange = data.percentageChange || 0;

          // NOUVEAU : Propriétés enrichies
          this.personalTransactionsStats.currentMonthWallet = data.currentMonthWallet || 0;
          this.personalTransactionsStats.currentMonthExternal = data.currentMonthExternal || 0;
          this.personalTransactionsStats.currentMonthTotal = data.currentMonthTotal || data.currentMonth || 0;

          this.personalTransactionsStats.previousMonthWallet = data.previousMonthWallet || 0;
          this.personalTransactionsStats.previousMonthExternal = data.previousMonthExternal || 0;
          this.personalTransactionsStats.previousMonthTotal = data.previousMonthTotal || data.previousMonth || 0;

          // Calculer les montants pour l'affichage
          this.calculatePersonalTransactionsAmounts();

          console.log("✅ Enhanced personal transaction statistics loaded:", this.personalTransactionsStats);

        } else {
          this.personalTransactionsStats.error = response.data.message || "Erreur lors du chargement";
          this.showAlert("warning", "Avertissement", response.data.message);
        }

      } catch (error) {
        console.error("❌ Error loading enhanced personal transactions stats:", error);

        // Fallback vers l'ancien endpoint si le nouveau n'existe pas
        console.log("🔄 Trying fallback to basic personal transactions stats...");
        await this.getPersonalTransactionsStatsBasic();

      } finally {
        this.personalTransactionsStats.loading = false;
      }
    },

    /**
     * Fallback method for basic personal transactions stats
     */
    async getPersonalTransactionsStatsBasic() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_personal_transactions_stats/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);

        if (response.data.status === 200) {
          const data = response.data.datas;

          this.personalTransactionsStats.currentMonth = data.currentMonth || 0;
          this.personalTransactionsStats.previousMonth = data.previousMonth || 0;
          this.personalTransactionsStats.difference = data.difference || 0;
          this.personalTransactionsStats.percentageChange = data.percentageChange || 0;

          // Estimation pour la répartition wallet/external si pas disponible
          this.personalTransactionsStats.currentMonthTotal = this.personalTransactionsStats.currentMonth;
          this.personalTransactionsStats.previousMonthTotal = this.personalTransactionsStats.previousMonth;

          this.calculatePersonalTransactionsAmounts();

          console.log("📊 Basic personal transactions stats loaded:", this.personalTransactionsStats);

        } else {
          this.personalTransactionsStats.error = response.data.message || "Erreur lors du chargement";
          this.showAlert("warning", "Avertissement", response.data.message);
        }

      } catch (error) {
        console.error("❌ Error loading basic personal transactions stats:", error);
        this.personalTransactionsStats.error = "Erreur de connexion";
        this.showAlert("error", "Erreur", "Impossible de charger les statistiques des transactions personnelles");
      }
    },

    /**
     * Enhanced method: Get unit price for transactions and load stats
     */
    getUnitPriceTransaction() {
      let str = `${this.$store.getters.user.baseUrl}/wallet/get_unit_price_transaction_resto/${this.balanceInfos.restaurantId}/${sessionStorage.getItem("accountId")}`;

      apiClient
          .get(str)
          .then(response => {
            if (response.data.status === 200) {
              this.unitPriceTransaction = response.data.datas;

              // CORRIGÉ : Utiliser l'endpoint existant qui fonctionne
              this.getPersonalTransactionsStats();

            } else {
              this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
              this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
              this.showAlert('warning', "Erreur", response.data.message);
            }
          })
          .catch(() => {
            this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
            this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
          });
    },

    // VERSION COMPLÈTE avec fallback intelligent pour RapportsRestaurantAdmin.vue
    async getPersonalTransactionsStats() {
      this.personalTransactionsStats.loading = true;
      this.personalTransactionsStats.error = null;

      // Utiliser EXACTEMENT le même endpoint que la version qui fonctionne
      const url = `${this.$store.getters.user.baseUrl}/wallet/personal-transactions/stats/${this.balanceInfos.companyId}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);

        if (response.data.status === 200) {
          const data = response.data.datas;

          console.log("📊 Personal transaction stats received:", data);

          // Mapper les données reçues
          this.personalTransactionsStats.currentMonthWallet = data.currentMonthWallet || 0;
          this.personalTransactionsStats.currentMonthExternal = data.currentMonthExternal || 0;
          this.personalTransactionsStats.currentMonthTotal = data.currentMonthTotal || 0;

          this.personalTransactionsStats.previousMonthWallet = data.previousMonthWallet || 0;
          this.personalTransactionsStats.previousMonthExternal = data.previousMonthExternal || 0;
          this.personalTransactionsStats.previousMonthTotal = data.previousMonthTotal || 0;

          this.personalTransactionsStats.difference = data.difference || 0;
          this.personalTransactionsStats.percentageChange = data.percentageChange || 0;

          // Calculer les stats enrichies
          this.calculateEnhancedStats(data);
          this.calculatePersonalTransactionsAmounts();

          console.log("✅ Enhanced personal transaction statistics loaded:", this.personalTransactionsStats);

        } else {
          // Si l'API retourne un statut d'erreur, utiliser le fallback
          console.log("⚠️ API returned error status, using fallback...");
          this.useDefaultPersonalTransactionsStats();
        }

      } catch (error) {
        console.error("❌ Error loading personal transactions stats:", error);

        // FALLBACK : Si l'endpoint échoue, utiliser des valeurs par défaut
        console.log("🔄 Using fallback with default values...");
        this.useDefaultPersonalTransactionsStats();

      } finally {
        this.personalTransactionsStats.loading = false;
      }
    },

    /**
     * Méthode de fallback avec valeurs par défaut basées sur les transactions existantes
     */
    useDefaultPersonalTransactionsStats() {
      try {
        // Analyser les transactions existantes pour estimer les valeurs
        const currentMonthTransactions = this.getEstimatedCurrentMonthTransactions();
        const previousMonthTransactions = this.getEstimatedPreviousMonthTransactions();

        this.personalTransactionsStats.currentMonthTotal = currentMonthTransactions;
        this.personalTransactionsStats.previousMonthTotal = previousMonthTransactions;
        this.personalTransactionsStats.difference = currentMonthTransactions - previousMonthTransactions;
        this.personalTransactionsStats.percentageChange = previousMonthTransactions > 0 ?
            ((currentMonthTransactions - previousMonthTransactions) / previousMonthTransactions * 100) : 0;

        // Estimation de la répartition wallet/external (25% wallet, 75% external)
        this.personalTransactionsStats.currentMonthWallet = Math.floor(currentMonthTransactions * 0.25);
        this.personalTransactionsStats.currentMonthExternal = Math.ceil(currentMonthTransactions * 0.75);

        this.personalTransactionsStats.previousMonthWallet = Math.floor(previousMonthTransactions * 0.25);
        this.personalTransactionsStats.previousMonthExternal = Math.ceil(previousMonthTransactions * 0.75);

        // Calculer les stats et montants
        this.calculateEnhancedStats();
        this.calculatePersonalTransactionsAmounts();

        console.log("🎯 Using estimated personal transaction stats:", this.personalTransactionsStats);

        // Afficher un message informatif à l'utilisateur
        this.showAlert("info", "Données estimées",
            "Les statistiques des transactions personnelles sont basées sur une estimation des données existantes.");

      } catch (error) {
        console.error("❌ Error in fallback method:", error);
        // Valeurs par défaut absolues en cas d'échec total
        this.setEmptyPersonalTransactionsStats();
      }
    },

    /**
     * Estimation des transactions personnelles du mois actuel
     */
    getEstimatedCurrentMonthTransactions() {
      if (!this.transactions || this.transactions.length === 0) return 0;

      const currentMonth = moment().format('YYYY-MM');
      const personalTransactions = this.transactions.filter(t =>
          t.niveau === 'SECONDAIRE_PERSONNEL' &&
          moment(t.dateCommande).format('YYYY-MM') === currentMonth
      );

      return personalTransactions.length;
    },

    /**
     * Estimation des transactions personnelles du mois précédent
     */
    getEstimatedPreviousMonthTransactions() {
      if (!this.transactions || this.transactions.length === 0) return 0;

      const previousMonth = moment().subtract(1, 'month').format('YYYY-MM');
      const personalTransactions = this.transactions.filter(t =>
          t.niveau === 'SECONDAIRE_PERSONNEL' &&
          moment(t.dateCommande).format('YYYY-MM') === previousMonth
      );

      return personalTransactions.length;
    },

    /**
     * Valeurs par défaut vides en cas d'échec total
     */
    setEmptyPersonalTransactionsStats() {
      this.personalTransactionsStats.currentMonthWallet = 0;
      this.personalTransactionsStats.currentMonthExternal = 0;
      this.personalTransactionsStats.currentMonthTotal = 0;
      this.personalTransactionsStats.previousMonthWallet = 0;
      this.personalTransactionsStats.previousMonthExternal = 0;
      this.personalTransactionsStats.previousMonthTotal = 0;
      this.personalTransactionsStats.difference = 0;
      this.personalTransactionsStats.percentageChange = 0;

      this.sensNumerikInfos.actualMonthEncours = "0 F CFA";
      this.sensNumerikInfos.previousMonthEncours = "0 F CFA";
    }
  }
}
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.v-data-table {
  font-size: 0.9rem;
}

/* Style pour les en-tetes de colonnes */
.v-data-table >>> thead tr th {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #1976D2 !important;
  background-color: #F5F5F5 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px !important;
}

/* Ajouter une bordure sous les en-tetes */
.v-data-table >>> thead tr {
  border-bottom: 2px solid #E0E0E0;
}

/* Style au survol des en-tetes */
.v-data-table >>> thead tr th:hover {
  background-color: #EEEEEE !important;
  cursor: pointer;
}

/* Amelioration de l'apparence des cartes */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Style pour les statistiques */
.text-caption {
  font-size: 0.75rem !important;
  color: #666;
}

/* Style pour les boutons de sélection de période */
.v-btn-toggle {
  flex-wrap: wrap;
  display: inline-flex !important;
}

.v-btn-toggle .v-btn {
  min-width: 120px;
  margin: 2px;
}

/* Centrer le contenu de la colonne */
.text-center {
  text-align: center;
}

.justify-center {
  justify-content: center;
}

/* Amélioration de la transition */
.v-expand-transition {
  transition: all 0.3s ease-in-out;
}

/* Styles responsive pour les onglets adaptés de RapportsRestaurant.vue */
.tabs-responsive {
  transition: all 0.3s ease;
}

/* Mobile styles */
@media (max-width: 959px) {
  .tabs-responsive >>> .v-tab {
    min-width: auto !important;
    padding: 0 8px !important;
    font-size: 0.75rem !important;
    flex-direction: row !important;
    text-transform: none !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    margin-bottom: 0 !important;
    margin-right: 4px !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    height: 48px !important;
  }

  .tabs-responsive >>> .v-tab--active {
    background-color: rgba(25, 118, 210, 0.1) !important;
  }
}

/* Tablet styles */
@media (min-width: 960px) and (max-width: 1263px) {
  .tabs-responsive >>> .v-tab {
    min-width: 120px !important;
    padding: 0 12px !important;
    font-size: 0.8rem !important;
  }
}

/* Desktop styles */
@media (min-width: 1264px) {
  .tabs-responsive >>> .v-tab {
    min-width: 160px !important;
    padding: 0 16px !important;
    font-size: 0.875rem !important;
  }
}

/* Amélioration de la lisibilité sur mobile */
@media (max-width: 599px) {
  .tabs-responsive >>> .v-tab {
    font-size: 0.7rem !important;
    padding: 0 6px !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    font-size: 18px !important;
  }
}

/* Scroll horizontal sur très petits écrans */
@media (max-width: 479px) {
  .tabs-responsive {
    overflow-x: auto !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    flex-wrap: nowrap !important;
  }

  .tabs-responsive >>> .v-tab {
    flex: 0 0 auto !important;
    white-space: nowrap !important;
  }
}

/* Responsive */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  h2 {
    font-size: 1.5rem !important;
  }
}

/* Animation pour les chips */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* Styles responsive pour les onglets adaptés de RapportsRestaurantAdmin.vue */
.tabs-responsive {
  transition: all 0.3s ease;
}

/* Mobile styles */
@media (max-width: 959px) {
  .tabs-responsive >>> .v-tab {
    min-width: auto !important;
    padding: 0 8px !important;
    font-size: 0.75rem !important;
    flex-direction: row !important;
    text-transform: none !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    margin-bottom: 0 !important;
    margin-right: 4px !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    height: 48px !important;
  }

  .tabs-responsive >>> .v-tab--active {
    background-color: rgba(25, 118, 210, 0.1) !important;
  }
}

/* Tablet styles */
@media (min-width: 960px) and (max-width: 1263px) {
  .tabs-responsive >>> .v-tab {
    min-width: 120px !important;
    padding: 0 12px !important;
    font-size: 0.8rem !important;
  }
}

/* Desktop styles */
@media (min-width: 1264px) {
  .tabs-responsive >>> .v-tab {
    min-width: 160px !important;
    padding: 0 16px !important;
    font-size: 0.875rem !important;
  }
}

/* Amélioration de la lisibilité sur mobile */
@media (max-width: 599px) {
  .tabs-responsive >>> .v-tab {
    font-size: 0.7rem !important;
    padding: 0 6px !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    font-size: 18px !important;
  }
}

/* Scroll horizontal sur très petits écrans */
@media (max-width: 479px) {
  .tabs-responsive {
    overflow-x: auto !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    flex-wrap: nowrap !important;
  }

  .tabs-responsive >>> .v-tab {
    flex: 0 0 auto !important;
    white-space: nowrap !important;
  }
}

/* Enhanced mobile data table styles */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  h2 {
    font-size: 1.5rem !important;
  }

  /* Masquer certaines colonnes sur mobile */
  .v-data-table >>> th:nth-child(6),
  .v-data-table >>> td:nth-child(6),
  .v-data-table >>> th:nth-child(7),
  .v-data-table >>> td:nth-child(7) {
    display: none;
  }

  /* Adapter la taille des avatars sur mobile */
  .v-avatar {
    width: 24px !important;
    height: 24px !important;
  }

  /* Réduire la taille des chips sur mobile */
  .v-chip--small {
    font-size: 0.6rem !important;
    height: 20px !important;
  }
}

/* Animation pour les chips */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* CSS supplémentaire pour améliorer la responsivité */
.filters-row {
  min-height: 60px;
}

/* Assurer que les champs ne débordent pas */
.v-text-field, .v-select {
  max-width: 100%;
}

/* Améliorer l'espacement sur petits écrans */
@media (max-width: 960px) {
  .filters-row .v-col {
    padding: 6px 12px;
  }
}

/* Responsive pour tablettes */
@media (min-width: 768px) and (max-width: 1023px) {
  .filters-row .v-col {
    padding: 8px 12px;
  }
}

/* Assurer une largeur minimale pour éviter l'entrelacement */
.v-select >>> .v-input__control {
  min-width: 180px;
}

.v-text-field >>> .v-input__control {
  min-width: 200px;
}

/* Styles pour les onglets - version simplifiée */
@media (max-width: 960px) {
  .v-tabs >>> .v-tab {
    flex-direction: row !important;
    min-width: auto !important;
    padding: 0 12px !important;
  }

  .v-tabs >>> .v-tab .v-icon {
    margin-bottom: 0 !important;
    margin-right: 8px !important;
  }

  .v-tabs >>> .v-tabs-bar {
    justify-content: center !important;
  }
}

@media (max-width: 600px) {
  .v-tabs >>> .v-tab {
    font-size: 0.75rem !important;
    padding: 0 8px !important;
  }

  .v-tabs >>> .v-tab .v-icon {
    font-size: 18px !important;
    margin-right: 4px !important;
  }
}
</style>